// English is the source locale: every key must exist here first.
// pl.ts is type-checked against this shape, so a missing translation fails CI.
const en = {
  app: {
    name: "Patronix",
    switchOrganization: "Switch organization",
    switchCompany: "Switch company or organization",
    companies: "Companies",
    organizations: "Organizations",
    signOut: "Sign out",
    version: "Version",
    versionCopied: "Version copied",
    commit: "Commit",
    builtAt: "Built",
    deployed: "Deployed {{time}}",
    demoMode: "Demo mode",
  },
  auth: {
    signInTitle: "Sign in to Patronix",
    signInSubtitle: "Welcome back. Use your work email.",
    signUpTitle: "Create your account",
    signUpSubtitle: "Patronix is invitation only. Use the email address you were invited with.",
    email: "Email",
    password: "Password",
    fullName: "Full name",
    signIn: "Sign in",
    signUp: "Create account",
    contact: "Questions or a demo?",
    inviteOnlyNote: "Access is by invitation only. Ask your administrator for an account.",
    haveAccount: "Already have an account?",
    switchToSignIn: "Sign in",
    localHint: "Local demo: demo@patronix.local / patronix-demo",
    checkEmail: "Check your inbox to confirm your email, then sign in.",
    passwordHint: "At least 8 characters.",
    errorGeneric: "Something went wrong. Please try again.",
    invitationInvalid: "You are signed in, but this invitation is not valid for your account (other email, expired, revoked or already used).",
    inviteOnly: "This email address has no invitation. Ask your administrator to invite you.",
  },
  onboarding: {
    title: "Set up your organization",
    subtitle: "You can add more companies and invite people later.",
    orgName: "Organization name",
    orgNamePlaceholder: "e.g. Acme Seating",
    slug: "Short name",
    slugHint: "Lowercase letters, digits and dashes. Used in links.",
    companyCode: "Company code",
    companyCodeHint: "Up to 16 characters, e.g. PL01.",
    companyName: "Company name",
    companyNamePlaceholder: "e.g. Acme Łódź",
    currency: "Currency",
    country: "Country",
    submit: "Create organization",
    slugTaken: "This short name is already taken.",
    limitReached: "You have reached the limit of organizations you can create.",
  },
  statusBar: {
    label: "Status bar",
    session: "Session",
    copied: "Copied",
  },
  nav: {
    dashboard: "Dashboard",
    main: "Main navigation",
    home: "Home",
    modules: "Modules",
    searchPlaceholder: "Search menu or session code…",
    noMatches: "No menu items match",
    settings: "Settings",
    masterData: {
      title: "Master data",
      items: "Items",
      units: "Units of measure",
      partners: "Business partners",
      lots: "Lots",
      serials: "Serial numbers",
    },
    sales: {
      title: "Sales",
      orders: "Sales orders",
      picking: "Picking",
      shipments: "Shipments",
      customers: "Customers",
      priceLists: "Price lists",
      discounts: "Discounts",
      returns: "Returns",
    },
    procurement: {
      title: "Procurement",
      purchaseOrders: "Purchase orders",
      receipts: "Receipts",
      receiving: "Receiving desk",
      suppliers: "Suppliers",
      purchasePriceLists: "Purchase price lists",
      supplierReturns: "Supplier returns",
    },
    warehousing: {
      title: "Warehousing",
      inventory: "Inventory",
      warehouseOrders: "Warehouse orders",
      adjustments: "Adjustments",
      adjustmentReasons: "Adjustment reasons",
      transfers: "Transfers",
      cycleCounts: "Cycle counts",
      warehouses: "Warehouses",
      locations: "Locations",
    },
    engineering: {
      title: "Engineering",
      engineeringBoms: "Engineering BOMs (EBOM)",
      boms: "Manufacturing BOMs (MBOM)",
      productionBoms: "Production BOMs (PBOM)",
      bomStructure: "BOM structure",
      changes: "Engineering changes",
    },
    manufacturing: {
      workbench: "Operator workbench",
      title: "Manufacturing",
      productionOrders: "Production orders",
      kits: "Kits",
      routings: "Routings",
      workCenters: "Work centers",
    },
    planning: {
      title: "Planning",
      mrp: "MRP run",
      plannedOrders: "Planned orders",
      suggestions: "Agent suggestions",
      masterPlan: "Master plan",
      stockCoverage: "Stock coverage",
    },
    quality: {
      title: "Quality",
      inspectionOrders: "Inspection orders",
      nonconformances: "Nonconformance reports",
      inspectionPlans: "Inspection plans",
      characteristics: "Characteristics",
    },
    project: {
      title: "Project",
      projects: "Projects",
      progress: "Progress",
      myTime: "My time",
    },
    groups: {
      overview: "Overview",
      items: "Items",
      partners: "Business partners",
      orders: "Orders",
      outbound: "Outbound",
      inbound: "Inbound",
      masterData: "Master data",
      inventory: "Inventory",
      inventoryChange: "Inventory change orders",
      processData: "Process data",
      warehouseStructure: "Warehouse structure",
      qualityData: "Quality data",
      changes: "Changes",
      productStructures: "Product structures",
      engineering: "Engineering data",
      mrp: "Material planning",
      analysis: "Analysis",
      inspections: "Inspections",
      projects: "Projects",
      time: "Time",
      organization: "Organization",
      system: "System",
    },
    settingsGroup: {
      title: "Settings",
      companies: "Companies & sites",
      users: "Users & roles",
      security: "Security",
      agents: "Agents",
      numbering: "Numbering series",
      auditLog: "Audit log",
      processMap: "Process map",
    },
  },
  agents: {
    inboxTitle: "Agent suggestions",
    inboxHint: "What the agents propose. Nothing changes until someone with the permission accepts it.",
    listLabel: "Suggestion list",
    detailLabel: "Suggestion details",
    colTime: "Proposed",
    colTitle: "Suggestion",
    colAgent: "Agent",
    colCompany: "Company",
    colStatus: "Status",
    status: {
      proposed: "Open",
      executed: "Accepted",
      rejected: "Rejected",
      expired: "Expired",
      superseded: "Superseded",
    },
    code: { purchasing: "Purchasing agent" },
    about: {
      purchasing:
        "After every MRP run and each morning it groups the planned purchase orders due within the horizon per supplier and proposes converting them into draft purchase orders.",
    },
    kind: {
      convert_planned_orders_one: "Convert {{count}} planned order into a draft purchase order",
      convert_planned_orders_few: "Convert {{count}} planned orders into draft purchase orders",
      convert_planned_orders_many: "Convert {{count}} planned orders into draft purchase orders",
      convert_planned_orders_other: "Convert {{count}} planned orders into draft purchase orders",
    },
    accept: { convert_planned_orders: "Create draft orders" },
    facts: {
      title_one: "Order from {{supplier}}: {{count}} line",
      title_few: "Order from {{supplier}}: {{count}} lines",
      title_many: "Order from {{supplier}}: {{count}} lines",
      title_other: "Order from {{supplier}}: {{count}} lines",
      summary_one: "{{count}} planned purchase order for {{supplier}}, release from {{release}}, due {{from}} – {{to}}.",
      summary_few: "{{count}} planned purchase orders for {{supplier}}, release from {{release}}, due {{from}} – {{to}}.",
      summary_many: "{{count}} planned purchase orders for {{supplier}}, release from {{release}}, due {{from}} – {{to}}.",
      summary_other: "{{count}} planned purchase orders for {{supplier}}, release from {{release}}, due {{from}} – {{to}}.",
      late_one: "{{count}} is already past its release date.",
      late_few: "{{count}} are already past their release date.",
      late_many: "{{count}} are already past their release date.",
      late_other: "{{count}} are already past their release date.",
      firmed_one: "{{count}} firmed by a planner.",
      firmed_few: "{{count}} firmed by a planner.",
      firmed_many: "{{count}} firmed by a planner.",
      firmed_other: "{{count}} firmed by a planner.",
      waiting: "{{count}} more planned orders follow in the next suggestion (200 per order at most).",
      line: "· {{item}} {{qty}} {{uom}}, due {{due}}",
      more_one: "· … and {{count}} more",
      more_few: "· … and {{count}} more",
      more_many: "· … and {{count}} more",
      more_other: "· … and {{count}} more",
    },
    reject: "Reject",
    rejectNote: "Why (optional)",
    why: "Why",
    action: "What happens",
    outcome: "Outcome",
    company: "Company {{code}}",
    expires: "expires {{date}}",
    executedBy: "Accepted by {{name}}, {{date}}",
    rejectedBy: "Rejected by {{name}}",
    cannotDecide: "You can see this suggestion, but deciding it needs the permission of its action.",
    emptyTitle: "No suggestions",
    emptyBody: "Nothing in this status.",
    emptyBodyProposed: "The agents have nothing to propose right now.",
    selectPrompt: "Select a suggestion",
    selectPromptHint: "Its reasons and the action appear here.",
    manage: "Manage agents",
    openInbox: "Open agent suggestions",
    settingsTitle: "Agents",
    settingsHint: "Agents watch what happens in Patronix and propose work for people to accept. They hold no roles and change nothing on their own.",
    noPermission: "Seeing agents needs the core.agent.read permission.",
    mfaNote: "Agents sign in with a password. If Security requires two-step sign-in for everyone, agents cannot work; use \"admins only\" or \"off\".",
    enabled: "Enabled",
    disabled: "Disabled",
    notRegistered: "Not set up",
    askAdmin: "An administrator sets agents up.",
    step1: "Invite a dedicated account for the agent (e.g. agent+purchasing@your-company.com) and sign up with it once.",
    step2: "Remove all its roles in Users & roles: an agent holds none.",
    step3: "Choose it below; then enable the agent.",
    account: "Agent account",
    chooseAccount: "Choose an account…",
    noCandidates: "No member without roles",
    register: "Set up agent",
    enable: "Enabled",
    horizon: "Horizon (days)",
    lastRuns: "Last runs",
    noRuns: "No runs yet.",
    run: { running: "Running", succeeded: "Done", failed: "Failed", skipped: "Skipped" },
    trigger: { event: "after an event", schedule: "scheduled", manual: "started by hand", chat: "chat" },
    err: {
      decided: "Someone has already decided this suggestion.",
      expired: "The suggestion has expired.",
      permission: "You do not have the permission this action needs.",
      dedicated: "Use a dedicated account: this one also belongs to another organization.",
      hasRoles: "Remove the account's roles first: an agent holds no roles.",
      exists: "This agent is already set up.",
      horizon: "Horizon: 1 to 90 days.",
      plannedChanged: "The planned orders changed since the suggestion was made (converted or replanned). Reject it; the agent proposes again after the next run.",
    },
  },
  mfa: {
    verifyTitle: "Two-step sign-in",
    verifySubtitle: "Enter the 6-digit code from your authenticator app.",
    enrollTitle: "Set up two-step sign-in",
    enrollSubtitle: "Your organization requires an authenticator code at sign-in for your role.",
    step1: "Install an authenticator app (Microsoft Authenticator, Google Authenticator, 1Password, Bitwarden…).",
    step2: "Scan the QR code, or enter the key below by hand.",
    step3: "Type the 6-digit code the app shows.",
    qrAlt: "QR code to add Patronix to an authenticator app",
    copySecret: "Copy the key",
    code: "Code",
    codeHint: "6 digits, renewed every 30 seconds",
    codeInvalid: "Enter the 6 digits.",
    codeWrong: "That code did not match. Check the time on your phone and try the next code.",
    enrollFailed: "The authenticator could not be set up. Try again.",
    verify: "Verify",
    enable: "Turn on two-step sign-in",
    signOut: "Sign out",
  },
  security: {
    factorTooNew: "To make the policy less strict, use an authenticator set up at least 24 hours ago. Making it stricter works at once.",
    title: "Security",
    demo: "Two-step sign-in needs a database (not available in demo mode).",
    mineTitle: "My two-step sign-in",
    mineBody: "Sign in with your password and a code from an authenticator app on your phone.",
    on: "On",
    off: "Off",
    authenticators: "Authenticator apps",
    authenticator: "Authenticator",
    none: "No authenticator yet.",
    added: "added {{when}}",
    remove: "Remove",
    removeFailed: "The authenticator could not be removed.",
    lastRequired: "Your organization requires two-step sign-in: add another authenticator before removing this one.",
    enable: "Turn on two-step sign-in",
    addAnother: "Add another authenticator",
    backupHint: "Tip: add a second authenticator (another phone or a password manager) as a backup. If you lose all of them, the organization owner resets your account in Supabase.",
    nextSignIn: "The code is asked from your next sign-in.",
    orgTitle: "Organization policy",
    orgBody: "Who must sign in with an authenticator code. Changes apply from each member's next sign-in or token refresh (within an hour).",
    orgReadOnly: "Only an administrator of the organization can change the policy.",
    orgNeedsMfa: "Turn on your own two-step sign-in first: the policy can only be changed from a two-step session.",
    save: "Save policy",
    saveFailed: "The policy could not be saved.",
    policy: { off: "Optional", admins: "Required for administrators", all: "Required for everyone" },
    policyHint: {
      off: "Members choose themselves.",
      admins: "Members with the Administrator role must use it (recommended).",
      all: "Every member must use it.",
    },
  },
  ecosystem: {
    title: "Patronix apps",
    comingSoon: "Coming soon",
    close: "Close",
    apps: {
      data: "Database, data spaces and information structures",
      scan: "Mobile app for 1D / 2D warehouse scanners",
      doc: "Digital flow of documents, letters and invoices",
      view: "Business analytics, KPI dashboards and reporting",
      link: "Process router, integration bus and microservices",
      flow: "Visual BPMN process maps and live process view",
      inbox: "Tasks, approvals and notifications",
      health: "Health checks of APIs and servers",
      security: "Security settings, roles and permissions",
    },
  },
  topbar: {
    search: "Find an order, item or partner…",
    notifications: "Notifications",
    themeLight: "Switch to light mode",
    themeDark: "Switch to dark mode",
    language: "Change language",
    density: "Toggle compact density",
    collapseSidebar: "Hide menu",
    expandSidebar: "Show menu",
    guide: "Where am I? Process guide (?)",
  },
  palette: {
    placeholder: "Type a command or search…",
    empty: "No results.",
    goTo: "Go to",
    actions: "Actions",
    toggleTheme: "Toggle light / dark mode",
    toggleDensity: "Toggle compact density",
    switchLanguage: "Przełącz na polski",
  },
  home: {
    greetingMorning: "Good morning, {{name}}",
    greetingAfternoon: "Good afternoon, {{name}}",
    greetingEvening: "Good evening, {{name}}",
    demoNotice: "Sample data — demo mode without a database.",
    quickActions: "Quick actions",
    newPurchaseOrder: "New purchase order",
    receiveGoods: "Receive goods",
    startProduction: "Start production",
    transferStock: "Transfer stock",
    glance: "At a glance",
    period: "Period",
    periodDays: "{{n}} d",
    updated: "Updated {{time}}",
    preparing: "Preparing the numbers…",
    cardShipped: "Lines shipped",
    cardReceived: "Lines received",
    cardProduced: "Production receipts",
    cardNewOrders: "Sales orders released",
    hintOpenSo: "Open sales orders: {{n}}",
    hintOpenPo: "Open purchase orders: {{n}} · drafts: {{drafts}}",
    hintProduction: "Production orders in progress: {{n}}",
    hintPoReleased: "Purchase orders released: {{n}}",
    noPrevious: "no earlier data",
    vsPrevious: "vs the previous {{n}} days",
    flowTitle: "Flow of goods",
    flowSubtitle: "Ledger lines per day, last {{n}} days",
    flowAria: "Received, produced and shipped lines per day over the last {{n}} days",
    flowReceived: "Received",
    flowProduced: "Produced",
    flowShipped: "Shipped",
    seriesShown: "shown",
    seriesHidden: "hidden",
    flow_consumed: "Material issued",
    flow_transferred: "Transfers",
    flow_adjusted: "Adjustments and counts",
    serviceTitle: "Service level",
    serviceSubtitle: "Share on time or right first time, against the target",
    rate_otd: "On-time delivery",
    rate_supplier_otd: "Supplier on time",
    rate_schedule: "Schedule adherence",
    rate_fpy: "First-pass quality",
    rateOf: "{{value}} of {{base}}",
    rateNone: "nothing in the period",
    target: "target {{value}}%",
    pp: "pp",
    backlogTitle: "Backlog",
    backlogSubtitle: "Open and late now, daily trend",
    backlogSince: "Daily trend since {{day}}",
    backlog_late_so: "Sales orders late to ship",
    backlog_late_po: "Purchase orders overdue",
    backlog_late_mo: "Production orders past due",
    backlog_short_mo: "Production short of material",
    rhythmTitle: "Warehouse rhythm",
    rhythmSubtitle: "Postings by weekday and hour, last 4 weeks",
    rhythmAria: "{{total}} postings in 4 weeks, busiest on {{day}} at {{hour}}:00",
    rhythmCell: "{{day}} {{hour}}:00 — {{n}} postings",
    rhythmPeak: "Busiest: {{day}} {{hour}}:00–{{hour}}:59 ({{n}} postings)",
    rhythmEmpty: "No postings in the last 4 weeks.",
    loadFailed: "The dashboard could not be loaded.",
    retry: "Try again",
    attention: "Needs your attention",
    allClear: "Nothing needs your attention right now.",
    att: {
      overdue_receipts_one: "{{count}} purchase order overdue",
      overdue_receipts_few: "{{count}} purchase orders overdue",
      overdue_receipts_many: "{{count}} purchase orders overdue",
      overdue_receipts_other: "{{count}} purchase orders overdue",
      late_shipments_one: "{{count}} sales order late to ship",
      late_shipments_few: "{{count}} sales orders late to ship",
      late_shipments_many: "{{count}} sales orders late to ship",
      late_shipments_other: "{{count}} sales orders late to ship",
      material_shortages_one: "{{count}} production order short of material",
      material_shortages_few: "{{count}} production orders short of material",
      material_shortages_many: "{{count}} production orders short of material",
      material_shortages_other: "{{count}} production orders short of material",
      purchase_drafts_one: "{{count}} purchase order waiting for release",
      purchase_drafts_few: "{{count}} purchase orders waiting for release",
      purchase_drafts_many: "{{count}} purchase orders waiting for release",
      purchase_drafts_other: "{{count}} purchase orders waiting for release",
      counts_in_progress_one: "{{count}} cycle count in progress",
      counts_in_progress_few: "{{count}} cycle counts in progress",
      counts_in_progress_many: "{{count}} cycle counts in progress",
      counts_in_progress_other: "{{count}} cycle counts in progress",
      below_safety_one: "{{count}} item below safety stock",
      below_safety_few: "{{count}} items below safety stock",
      below_safety_many: "{{count}} items below safety stock",
      below_safety_other: "{{count}} items below safety stock",
      inspections_open_one: "{{count}} inspection waiting",
      inspections_open_few: "{{count}} inspections waiting",
      inspections_open_many: "{{count}} inspections waiting",
      inspections_open_other: "{{count}} inspections waiting",
      ncr_open_one: "{{count}} open nonconformance",
      ncr_open_few: "{{count}} open nonconformances",
      ncr_open_many: "{{count}} open nonconformances",
      ncr_open_other: "{{count}} open nonconformances",
    },
    attAction: {
      overdue_receipts: "Receive",
      late_shipments: "Ship",
      material_shortages: "Resolve",
      purchase_drafts: "Review",
      counts_in_progress: "Open",
      below_safety: "Plan",
      inspections_open: "Inspect",
      ncr_open: "Decide",
    },
    movements: "Latest stock movements",
    noMovements: "No stock movements yet.",
    reversal: "reversal",
    viewAll: "Stock",
    colTime: "Time",
    colItem: "Item",
    colType: "Type",
    colQty: "Qty",
  },
  movementType: {
    adjustment_in: "Adjustment +",
    adjustment_out: "Adjustment −",
    transfer_out: "Transfer out",
    transfer_in: "Transfer in",
    purchase_receipt: "Receipt",
    purchase_return: "Return to supplier",
    sales_issue: "Shipment",
    sales_return: "Customer return",
    production_issue: "Issue to order",
    production_receipt: "Finished goods",
    count_adjustment: "Count",
  },
  status: {
    decided: "Decided",
    open: "Open",
    posted: "Posted",
    planned: "Planned",
    in_progress: "In progress",
    completed: "Completed",
    draft: "Draft",
    released: "Released",
    partially_received: "Partially received",
    received: "Received",
    closed: "Closed",
    cancelled: "Cancelled",
    partially_shipped: "Partially shipped",
    shipped: "Shipped",
    approved: "Approved",
    obsolete: "Obsolete",
  },
  prod: {
    versions: "Versions",
    version: "Version",
    validity: "Valid",
    validFrom: "Valid from",
    validTo: "Valid to",
    open: "open",
    approvedBy: "Approved by",
    filterCurrent: "Current",
    approve: "Approve",
    newVersion: "New version",
    obsolete: "Make obsolete",
    deleteDraft: "Delete draft",
    confirmObsolete: "Make this version obsolete? It is no longer used for new orders.",
    confirmDelete: "Delete this draft? This cannot be undone.",
    err: {
      line: "Line {{line}}: {{message}}",
      stale: "Someone else changed this version meanwhile. Close the editor and open it again.",
      notManufactured: "Only manufactured items get BOMs and routings.",
      notDraft: "Only drafts can be changed or approved; create a new version instead.",
      notApproved: "Only approved versions can be made obsolete.",
      laterVersion: "A later approved version exists; make it obsolete first or choose a later valid-from date.",
      cycle: "The BOM would contain itself: {{path}}",
      noLines: "Add at least one component.",
      noOperations: "Add at least one operation.",
      componentInactive: "A component is inactive or a service.",
      workCenterInactive: "A work center is inactive.",
      validity: "Valid-to must be after valid-from.",
      baseQty: "The base quantity must be above zero.",
      supplyWarehouse: "The supply warehouse must belong to the work center's company.",
      duplicateCode: "This code is already used.",
      permission: "You do not have permission to manage production master data.",
      incompleteLine: "component and quantity are required",
      incompleteLineForm: "Every line needs a component and a quantity above zero.",
      selfComponent: "an item cannot be its own component",
      serviceComponent: "services cannot be components",
      componentInactiveLine: "the component is inactive",
      scrap: "scrap must be at least 0 and below 100 %",
      scrapForm: "Scrap is a percentage from 0 to below 100, at most 2 decimals.",
      incompleteOperation: "sequence, work center and description are required",
      incompleteOperationForm: "Every operation needs a sequence, a work center and a description.",
      timesForm: "Times are minutes, zero or more.",
      duplicateSeq: "the sequence appears twice",
      workCenterNotFound: "the work center is not in this company",
      workCenterInactiveLine: "the work center is inactive",
    },
  },
  mp: {
    examplesHint: "No kits here yet. Load planning examples: four kits and orders over the next weeks on the sample data (fully covered, short and locked), to try moving kits and checking coverage.",
    examplesHintSettings: "Planning examples: four kits and orders over the next weeks on the sample data, so the master plan shows covered, short and locked kits.",
    examplesLoad: "Load planning examples",
    examplesDone_one: "Loaded {{count}} kit with {{orders}} orders.",
    examplesDone_few: "Loaded {{count}} kits with {{orders}} orders.",
    examplesDone_many: "Loaded {{count}} kits with {{orders}} orders.",
    examplesDone_other: "Loaded {{count}} kits with {{orders}} orders.",
    examplesErr: {
      noSample: "Load the sample data first (Settings → Companies → Sample data).",
      otherCompany: "The sample data is in another company; switch to that company to load the examples.",
      changed: "The sample data was changed (items, warehouses or BOMs of DEMO-ST-600), so the examples cannot be built.",
      already: "The planning examples are already loaded in this company.",
      denied: "You need the right to manage production orders.",
      failed: "The examples could not be loaded. Try again.",
    },
    title: "Master plan",
    gridLabel: "Master plan",
    colPlan: "Kits and orders",
    looseOrders: "Orders without a kit ({{count}})",
    expand: "Expand {{number}}",
    collapse: "Collapse {{number}}",
    kitBar: "{{number}} {{name}}: {{start}} – {{due}}, coverage {{coverage}}. Drag or use ← → to move the whole kit.",
    orderBar: "{{number}} {{item}}: {{start}} – {{due}}, {{status}}, coverage {{coverage}}",
    bottleneck: "short: {{item}}",
    ctpMarker: "Earliest CTP {{date}}: move the kit here to clear the shortage",
    loadStrip: "Work-center load",
    componentStrip: "Component coverage",
    strip: "Bottom strip",
    noWorkCenters: "No work centers in this company.",
    noShortComponents: "Every component of the kits in this window is covered.",
    loadCell: "{{wc}} · {{day}}: {{load}} of {{cap}} min ({{pct}} %)",
    componentCell: "{{item}} · {{day}}: balance {{balance}} {{uom}} (in {{supply}}, out {{demand}})",
    componentHint: "Show the kits this shortage blocks",
    kitsBlocked: "{{count}} kits",
    supplyArrives: "supply arrives",
    kitMoved: "{{number}} moved by {{days}} d: {{moved}} orders re-dated, {{skipped}} started orders stayed.",
    coverageNow: "Coverage now {{pct}} %.",
    orderMoved: "{{number}} moved by {{days}} d with its subtree ({{moved}} orders).",
    earlier: "Earlier",
    later: "Later",
    today: "Today",
    zoom: "Zoom",
    zoom_day: "Days",
    zoom_week: "Weeks",
    emptyTitle: "Nothing planned in this window",
    emptyBody: "Kits and open production orders between these dates show here. Build kits under Manufacturing → Kits.",
    truncated: "Showing the first 100 kits and 300 loose orders of this window; narrow the window to see the rest.",
    legend: "Legend",
    legendCoverage: "Coverage",
    legendCtp: "earliest CTP",
    legendLoad: "Load",
    legendHint: "Drag a kit to move all its orders; drag an order to move it with the orders below it.",
  },
  kit: {
    title: "Kits",
    new: "New kit",
    listLabel: "Kits",
    detailLabel: "Kit",
    searchPlaceholder: "Search number or name",
    emptyTitle: "No kits",
    emptyBody: "Group the orders of one product structure into a kit to plan, move and check them together.",
    selectPrompt: "Select a kit",
    colNumber: "Kit",
    colCoverage: "Coverage",
    colAtp: "ATP status",
    colOrder: "Order",
    colDates: "Start → due",
    colActions: "Actions",
    name: "Name",
    nameOptional: "Empty: the item and order number",
    orders: "Orders",
    orderCount: "{{open}} / {{total}}",
    plannedStart: "Kit start",
    plannedDue: "Kit due",
    demand: "For",
    ctp: "Earliest CTP",
    coverage: "Kit coverage",
    atp: "Material availability (ATP / CTP)",
    check: "Check now",
    moveToCtp: "Move kit to {{date}}",
    lockHint: "A component has no stock or supply in time: orders at 0 % cannot be released (release lock).",
    shortages: "Shortages",
    component: "Component",
    short: "Short",
    availableOn: "Available",
    moreShortages: "{{count}} more shortages",
    source: { supply: "supply", lead_time: "lead time", unknown: "no supply, no lead time", non_nettable: "warehouse not in MRP" },
    status: { open: "Open", closed: "Closed" },
    atpStatus: { full_ok: "Fully covered", partial_shortage: "Partial shortage", critical_lock: "Release lock" },
    createMode: "How to build the kit",
    fromStructure: "From order structure",
    byHand: "By hand",
    fromStructureHint: "The order and every open order below it in the last MRP pegging, each linked to the order it supplies.",
    byHandHint: "Pick the orders; link them to their parents afterwards.",
    topOrder: "Top order",
    addOrder: "Add order",
    pickOrder: "Order number or item",
    noOrders: "No open orders found",
    removeOrder: "Remove {{number}}",
    create: "Create kit",
    moveKitTo: "Move the kit to start on",
    moveKit: "Move kit",
    shifted: "Kit moved by {{days}} d: {{moved}} orders moved, {{skipped}} started orders stayed.",
    violationsLeft: "{{count}} links broken by started orders.",
    orderMoved: "{{number}} moved by {{days}} d with its subtree ({{moved}} orders).",
    confirmTitle: "Moving {{number}} breaks the kit's integrity",
    violation: "{{number}} is due {{due}}, {{days}} d after {{parent}} starts ({{start}}, buffer {{offset}} d)",
    moveAnyway: "Move anyway",
    warnings: "Warnings",
    conflict: "{{number}} is due {{days}} d after {{demand}} needs it ({{date}})",
    tree: "Order structure",
    brokenPeg: "Due after its parent starts",
    move: "Move",
    moveOrder: "Move order",
    release: "Release",
    released: "{{number}} released: materials reserved.",
    releaseHint: "Release to production (coverage {{pct}} %)",
    releaseLocked: "Release lock: a component has no stock or supply by the start date",
    link: "Link",
    newStart: "New start",
    childHint: "Its children move with it; it must stay due before its parent starts.",
    subtreeHint: "Every order below it moves by the same days.",
    parent: "Parent order",
    root: "None (root)",
    offset: "Buffer (days before parent start)",
    close: "Close kit",
    err: {
      stale: "Someone changed this kit meanwhile. Reload it and try again.",
      releaseLock: "{{message}}",
      cycle: "These links would form a cycle.",
      parent: "The parent must be another order of this kit.",
      inKit: "{{message}}",
      size: "A kit holds 1 to 200 orders.",
      name: "Give the kit a name (up to 120 characters).",
      date: "Pick a date within a year back and ten years ahead.",
      notMovable: "Only planned or released orders move.",
      closeOpen: "A kit closes when none of its orders is open.",
      closed: "The kit is closed.",
      pickTop: "Pick the top order.",
      pickOrders: "Add at least one order.",
      offset: "Buffer: 0 to 365 days.",
    },
  },
  wc: {
    title: "Work centers",
    listLabel: "Work center list",
    detailLabel: "Work center details",
    selectPrompt: "Select a work center",
    new: "New work center",
    code: "Code",
    codeHint: "Upper-case letters, digits and . _ - (at most 20).",
    name: "Name",
    kind: "Kind",
    kinds: { machine: "Machine", line: "Line", team: "Team", subcontractor: "Subcontractor" },
    subcontractHint: "Operations at this work center run at a supplier: releasing a production order creates a subcontract purchase order line, receiving it reports the operation.",
    supplier: "Subcontractor (supplier)",
    serviceItem: "Service bought",
    subcontractPrice: "Price per unit",
    subcontractPriceHint: "Empty = the last price paid.",
    subcontractLead: "Lead time (days)",
    subcontractorWarehouse: "Subcontractor warehouse",
    subcontractorWarehouseHint: "Our stock at the supplier; materials of the operation are supplied from it.",
    supply: "Supplied from",
    supplyWarehouse: "Supply warehouse",
    supplyLocation: "Supply location",
    supplyHint: "Where backflushed components are taken from.",
    hoursPerDay: "Hours / day",
    active: "Active",
    inactive: "Inactive",
    showInactive: "Show inactive",
    emptyTitle: "No work centers yet",
    emptyBody: "Work centers are the machines, lines and teams that routings use.",
    err: {
      hours: "Hours per day: 0 to 24, at most 2 decimals.",
      code: "Code: upper-case letters, digits and . _ - (at most 20).",
      name: "Enter a name.",
      subcontractor: "A subcontractor needs a supplier and a service item.",
      price: "Price: zero or more, at most 4 decimals.",
      lead: "Lead time: 0 to 365 days.",
    },
  },
  sub: {
    itemSubcontracting: "Item subcontracting",
    materialAt: "material at {{code}} · sent / consumed / required",
    subcontracted: "Subcontracted",
    subcontracting: "Subcontracting",
    poOnRelease: "PO line on release",
    received: "received {{received}} / {{ordered}}",
    operation: "op {{seq}} {{description}}",
    lineFor: "{{order}} · op {{seq}} {{description}}",
    good: "Good",
    scrap: "Scrap",
    goodOf: "Good quantity for {{order}}",
    scrapOf: "Scrap quantity for {{order}}",
    receiveWork: "Receive work",
    sent: "sent {{sent}} / {{required}} {{uom}} to {{warehouse}}",
    sendAction: "Send material",
    sendTitle: "Send material to the subcontractor",
    sendRoute: "{{order}} · op {{seq}} · {{from}} → {{to}}",
    sendLineRoute: "{{order}} · line {{line}} {{item}} · {{from}} → {{to}}",
    sendLineHint: "One stock transfer into the subcontractor warehouse; receiving the item consumes the material there.",
    nothingToSendLine: "Everything this line needs is already at the subcontractor.",
    consumesAt: "consumes its material at {{code}}",
    toSend: "To send",
    sendQty: "Quantity of {{item}} to send from {{location}}",
    nothingToSend: "Everything this operation needs is already at the subcontractor.",
    sendHint: "One stock transfer into the subcontractor warehouse; the order's reservations move with the material.",
    postSend: "Send",
    err: {
      sendEmpty: "Enter a quantity to send.",
      operationDone: "This operation is done; no more material is sent for it.",
      noWarehouse: "The subcontractor warehouse is missing, inactive, the order's own warehouse or in another site.",
      notSupplied: "This material is not supplied from the subcontractor warehouse.",
      notOperation: "This material does not belong to the operation.",
      notLine: "This material does not belong to the order line.",
    },
  },
  eco: {
    title: "Engineering changes",
    subtitle: "A change order: the items it changes, their next engineering BOM revisions, released and transferred to production together.",
    new: "New engineering change",
    listLabel: "Engineering change list",
    detailLabel: "Engineering change details",
    selectPrompt: "Select an engineering change",
    emptyTitle: "No engineering changes",
    emptyBody: "Raise a change for the items whose design changes; their next revisions are released together.",
    searchPlaceholder: "Number, title or item code",
    filterOpen: "Open",
    colNumber: "Number",
    colItems: "Items",
    titleField: "Title",
    reason: "Reason",
    effective: "Effective from",
    effectiveHint: "The new manufacturing BOM versions are valid from this date",
    created: "Created",
    item: "Item",
    items: "Items",
    addItem: "Add item",
    removeItem: "Remove item {{line}}",
    noteOf: "Note of item {{line}}",
    itemsHint: "Each item carries its next engineering BOM revision: open it to change the components.",
    editorHint: "Saving gives each item its next revision: its draft, else a copy of the released one, else a new empty one.",
    editTitle: "Engineering change",
    noComponents: "The revision has no components yet",
    mbomVersion: "MBOM v{{version}}",
    review: "To review",
    pbomFits: "fits",
    ordersHint_one: "{{count}} open production order on an older BOM version (not changed automatically):",
    ordersHint_few: "{{count}} open production orders on an older BOM version (not changed automatically):",
    ordersHint_many: "{{count}} open production orders on an older BOM version (not changed automatically):",
    ordersHint_other: "{{count}} open production orders on an older BOM version (not changed automatically):",
    status: { draft: "Draft", approved: "Approved", implemented: "Implemented", closed: "Closed", cancelled: "Cancelled" },
    confirm: {
      approve: "Approve this change? Its revisions are frozen until it is implemented or reopened.",
      reopen: "Reopen this change? It goes back to draft and its revisions can be edited again.",
      implement: "Implement this change? Its revisions are released and the manufactured items get draft manufacturing BOM versions.",
      close: "Close this change?",
      cancel: "Cancel this change? Its draft revisions stay, detached.",
    },
    action: { approve: "Approve", reopen: "Reopen", implement: "Implement", close: "Close", cancel: "Cancel change", edit: "Edit" },
    err: {
      conflict: "Someone else changed this engineering change; reload it and try again.",
      notDraft: "Only a draft change can be edited.",
      title: "Enter a title (up to 160 characters).",
      date: "The effective date must be within a year back and ten years ahead.",
      items: "Add at least one item (up to 200).",
      permission: "You do not have permission for this.",
    },
  },
  pbom: {
    title: "Production BOMs (PBOM)",
    subtitle: "The order structure of an item: which manufactured levels become their own orders in a kit.",
    new: "New production BOM",
    listLabel: "Production BOM list",
    detailLabel: "Production BOM details",
    selectPrompt: "Select a production BOM",
    emptyTitle: "No production BOMs",
    emptyBody: "Define which levels of an item get their own production orders, then create kits from it.",
    filterCurrent: "Current",
    colVersion: "Version",
    colNodes: "Orders per kit",
    colKits: "Kits",
    colLevel: "Level",
    colQtyPer: "Per parent",
    colLead: "Lead",
    colBuffer: "Buffer (days)",
    approved: "Approved",
    versionShort: "v{{version}}",
    versions: "Versions",
    item: "Item",
    structure: "Order structure",
    structureHint: "Each level is its own production order in a kit, due its buffer before its parent starts. Levels not listed are built inside their parent's order.",
    editorHint_one: "{{count}} order per kit. Untick a level to build it inside its parent's order.",
    editorHint_other: "{{count}} orders per kit. Untick a level to build it inside its parent's order.",
    editorHint_few: "{{count}} orders per kit. Untick a level to build it inside its parent's order.",
    editorHint_many: "{{count}} orders per kit. Untick a level to build it inside its parent's order.",
    noMbom: "The item has no approved manufacturing BOM valid today; approve one first.",
    truncated: "The structure has more than 500 levels; only the first 500 are shown.",
    lost_one: "{{count}} level of the saved version no longer fits the manufacturing BOM and was left out.",
    lost_other: "{{count}} levels of the saved version no longer fit the manufacturing BOM and were left out.",
    lost_few: "{{count}} levels of the saved version no longer fit the manufacturing BOM and were left out.",
    lost_many: "{{count}} levels of the saved version no longer fit the manufacturing BOM and were left out.",
    ownOrder: "{{item}} as its own order",
    bufferOf: "Buffer days of {{item}}",
    bufferShort: "buffer {{days}} d",
    days: "{{days}} d",
    kits: "Kits from this version",
    editTitle: "Production BOM",
    nextTitle: "Next version",
    nextHint: "A new draft version, prefilled from version {{version}}.",
    kitTitle: "Create kit",
    kitQty: "Quantity ({{uom}})",
    kitDue: "Due date",
    kitName: "Kit name",
    kitNameHint: "Empty = item and due date",
    kitHint: "One planned production order per level: quantities from the manufacturing BOM, dates back from the due date by lead time and buffer, each order pegged to its parent.",
    kitCreate: "Create kit",
    confirm: {
      approve: "Approve this version? The current approved version becomes obsolete.",
      obsolete: "Withdraw this version? No new kits can be made from it.",
      delete: "Delete this draft?",
    },
    action: { approve: "Approve", obsolete: "Make obsolete", delete: "Delete", edit: "Edit", next: "New version", kit: "Create kit" },
    err: {
      draftExists: "This item already has a draft production BOM; change that one.",
      conflict: "Someone else changed this production BOM; reload it and try again.",
      notDraft: "Only a draft version can be changed; start a new version.",
      notManufactured: "Only an active manufactured item has a production BOM.",
      notApproved: "Only an approved production BOM makes kits.",
      qty: "Enter a quantity above zero.",
      qtyDecimals: "The quantity has more decimals than the unit allows.",
      date: "The due date must be within a year back and ten years ahead.",
      past: "The kit would start on {{date}}, in the past; choose a due date at least {{days}} days ahead.",
      structure: "The production BOM no longer fits the manufacturing BOMs: {{problem}}. Start a new version.",
      noStructure: "The item has no manufacturing BOM structure to build from.",
      buffer: "Buffer of {{item}}: whole days, 0 to 365.",
      permission: "You do not have permission for this.",
    },
  },
  ebom: {
    title: "Engineering BOMs",
    new: "New engineering BOM",
    item: "Item",
    listLabel: "Engineering BOM list",
    detailLabel: "Engineering BOM details",
    selectPrompt: "Select a revision to see its structure",
    emptyTitle: "No engineering BOMs match",
    emptyBody: "Try another status or clear the search.",
    filterCurrent: "Current",
    colRev: "Rev.",
    colLines: "Lines",
    revShort: "rev. {{rev}}",
    revisions: "Revisions",
    changeNo: "Change no.",
    changeNoHint: "The engineering change (ECN / ECO) behind this revision.",
    released: "Released",
    findNo: "Find no.",
    findNoOf: "Find number of line {{line}}",
    reference: "Reference",
    referenceOf: "Reference of line {{line}}",
    linesHint: "Quantity per one item in the component's base unit. Find no. = position on the drawing (default 10, 20 …). No production data here: scrap, issue method and operations belong to the manufacturing BOM.",
    editTitle: "Edit the draft revision",
    mbom: "Manufacturing BOM",
    mbomVersion: "v{{version}}",
    mbomFrom: "from rev. {{rev}}",
    mbomNoSource: "not from an engineering BOM",
    mbomNone: "The item has no manufacturing BOM yet.",
    colMbom: "MBOM",
    mbomState: { none: "No MBOM", linked: "Up to date", behind: "Behind" },
    transfer: "Transfer to production",
    transferHint: "Creates a draft manufacturing BOM version from this revision: components and quantities from here (× the current MBOM's base quantity), scrap, issue method and operations kept from the current MBOM, its production-only lines (packaging, consumables) carried over.",
    transferNotManufactured: "Only a manufactured item has a manufacturing BOM.",
    compare: "Compare with the manufacturing BOM",
    compareEmpty: "Nothing to compare.",
    colEbomQty: "EBOM",
    colMbomQty: "MBOM",
    compareState: { same: "Same", qty: "Different quantity", ebom_only: "Only in EBOM", mbom_only: "Only in MBOM" },
    compareHint: "Quantities per one item in the base unit.",
    nextTitle: "Next revision",
    nextHint: "A new draft revision, prefilled from rev. {{rev}}. Releasing it makes rev. {{rev}} obsolete.",
    action: {
      edit: "Edit",
      release: "Release",
      delete: "Delete draft",
      next: "New revision",
      obsolete: "Make obsolete",
    },
    confirm: {
      release: "Release this revision? It becomes read-only and the previous released revision becomes obsolete.",
      delete: "Delete this draft revision?",
      obsolete: "Withdraw this released revision without a successor?",
    },
    err: {
      draftExists: "This item already has a draft revision — open and change that one.",
      conflict: "Someone else changed this revision. Reload it and try again.",
      notDraft: "Only a draft revision can be changed. Start a new revision.",
      noLines: "Add at least one component before releasing.",
      service: "A service has no engineering BOM.",
      cycle: "The structure would contain itself: {{path}}",
      permission: "You need the right to manage engineering BOMs.",
    },
  },
  bom: {
    multiLevel: "Multi-level",
    explosion: "All levels",
    whereUsedAll: "Where used, all levels",
    colLevel: "Level",
    colQtyPer: "Per parent",
    colQtyTotal: "Total",
    colVersion: "BOM version",
    colLeadTime: "Lead time (days)",
    colCumLeadTime: "Cumulative lead time (days)",
    colLeadShort: "Lead",
    explodeQty: "Quantity",
    explodeDate: "Valid on",
    explosionSummary: "{{levels}} levels · {{rows}} components · cumulative lead time {{days}} d",
    truncated: "Shown in part (too many rows)",
    openBom: "Open this BOM",
    missingBom: "no approved BOM",
    cycle: "cycle",
    days: "{{n}} d",
    noExplosion: "No components in an approved version valid on this date.",
    topItem: "top",
    notUsed: "Not used in any approved BOM valid on this date.",
    title: "Manufacturing BOMs (MBOM)",
    listLabel: "BOM list",
    fromEbom: "From engineering BOM",
    noEbom: "Not from an engineering BOM",
    behindEbom: "Engineering released rev. {{rev}}; this version follows an older structure. Transfer rev. {{rev}} from the engineering BOM.",
    openEbom: "Open engineering BOMs",
    detailLabel: "BOM details",
    selectPrompt: "Select a BOM version",
    new: "New BOM",
    item: "Manufactured item",
    colItem: "Item",
    colLines: "Components",
    searchPlaceholder: "Item code or description…",
    emptyTitle: "No BOMs match",
    baseQty: "Base quantity",
    components: "Components",
    component: "Component",
    noLines: "No components yet.",
    manufactured: "Manufactured",
    issue: { manual: "Manual issue", backflush: "Backflush" },
    scrap: "Scrap %",
    scrapShort: "scrap {{pct}} %",
    operation: "Op.",
    operationShort: "op. {{seq}}",
    qtyOf: "Quantity of line {{line}}",
    scrapOf: "Scrap of line {{line}}",
    issueMethodOf: "Issue method of line {{line}}",
    operationOf: "Operation of line {{line}}",
    linesHint: "Quantities per base quantity of the item, in the component's base unit. Backflush lines are consumed when finished goods are reported.",
    whereUsed: "Used in",
    err: { pickItem: "Choose a manufactured item." },
  },
  routing: {
    title: "Routings",
    listLabel: "Routing list",
    detailLabel: "Routing details",
    selectPrompt: "Select a routing version",
    new: "New routing",
    colOperations: "Operations",
    emptyTitle: "No routings match",
    operations: "Operations",
    runPerUnit: "Run time per unit",
    minutes: "{{value}} min",
    setupShort: "setup {{value}} min",
    runShort: "run {{value}} min / unit",
    seq: "Sequence",
    description: "Description",
    descriptionOf: "Description of operation {{seq}}",
    workCenterOf: "Work center of operation {{seq}}",
    setupOf: "Setup minutes of operation {{seq}}",
    runOf: "Run minutes per unit of operation {{seq}}",
    pickWorkCenter: "Work center…",
    setup: "Setup min",
    run: "Run min / unit",
    addOperation: "Add operation",
    timesHint: "Setup once per order; run time per unit of the item's base unit.",
  },
  prd: {
    productLabel: "Product label",
    boxLabels: "Box labels",
    title: "Production orders",
    listLabel: "Production order list",
    detailLabel: "Production order details",
    new: "New production order",
    searchPlaceholder: "Number, item code or description…",
    emptyTitle: "No production orders match",
    colQty: "Reported / planned",
    colMaterials: "Materials",
    quantity: "Quantity",
    reported: "Reported good",
    startDate: "Start",
    dueDate: "Due",
    warehouse: "Warehouse",
    versions: "Versions",
    routingShort: "routing",
    materials: "Materials",
    plannedMinutes: "setup {{setup}} min · run {{run}} min",
    reservedIssued: "reserved {{reserved}} · issued {{issued}}",
    shortLines_one: "{{count}} material line is not fully reserved.",
    shortLines_few: "{{count}} material lines are not fully reserved.",
    shortLines_many: "{{count}} material lines are not fully reserved.",
    shortLines_other: "{{count}} material lines are not fully reserved.",
    bomVersion: "BOM version",
    bomAuto: "Automatic (valid on the start date)",
    routingAuto: "The routing valid on the start date is used.",
    noRouting: "No approved routing — the order has no operations.",
    releasedHint: "After release only the due date, the tolerance and the note can change.",
    release: "Release",
    cancel: "Cancel order",
    complete: "Complete",
    close: "Close",
    confirmCancel: "Cancel this production order? Its material reservations are freed.",
    confirmComplete: "Complete this order? Open material reservations are freed and no more reports are expected.",
    issueTitle: "Issue materials",
    issueQty: "Quantity of {{item}} from {{location}}",
    nothingToIssue: "No open manual material lines.",
    issueHint: "Proposed from the line's supply location first, then FEFO. Lower or clear quantities to issue less.",
    postIssue: "Post issue",
    reportTitle: "Report production",
    reportLimit: "Open {{open}} {{uom}} · at most {{limit}} {{uom}} with tolerance",
    good: "Good",
    scrap: "Scrap",
    operation: "Operation",
    noOperation: "None",
    minutes: "Minutes",
    lot: "Lot",
    lotHint: "Empty = {{number}}",
    expiry: "Expiry date",
    backflushHint_one: "{{count}} backflush line is consumed for good + scrap.",
    backflushHint_few: "{{count}} backflush lines are consumed for good + scrap.",
    backflushHint_many: "{{count}} backflush lines are consumed for good + scrap.",
    backflushHint_other: "{{count}} backflush lines are consumed for good + scrap.",
    postReport: "Post report",
    operationTitle: "Report operation",
    operationHint: "Progress only: no stock movement.",
    postOperation: "Post progress",
    postings: "Postings",
    kind: { issue: "Material issue", report: "Production report", operation: "Operation progress" },
    postingQty: "good {{good}} · scrap {{scrap}} {{uom}}",
    moreLines_one: "+{{count}} more line",
    moreLines_few: "+{{count}} more lines",
    moreLines_many: "+{{count}} more lines",
    moreLines_other: "+{{count}} more lines",
    confirmReverse: "Reverse this posting?",
    olderPostings: "Older postings are not shown.",
    scrapShort: "scrap {{qty}}",
    toleranceShort: "tolerance {{pct}} %",
    operationProgress: "reported good {{good}} · scrap {{scrap}} · {{minutes}} min",
    reportAction: "Report production",
    issueAction: "Issue",
    operationAction: "Operation",
    issuedVariance: "issued {{issued}} · variance {{variance}}",
    tolerance: "Over-report tolerance %",
    toleranceHint: "How much more than planned may be reported (0–100).",
    err: {
      issueEmpty: "Enter a quantity on at least one line.",
      qtyFormat: "Quantities are numbers, zero or more.",
      reportEmpty: "Enter a good or scrap quantity (or minutes) above zero.",
      notPostable: "Postings need a released or in-progress order.",
      overReport: "That is more than planned plus the order's tolerance.",
      backflushShort: "Not enough stock for backflush: {{detail}}",
      reservedForOthers: "This stock is reserved for other orders; reserve this order first or issue less.",
      decimals: "The quantity has more decimals than the unit allows.",
      reportNegative: "Quantities and minutes cannot be negative.",
      notLot: "This item is not lot-controlled.",
      tolerance: "The tolerance is a percentage from 0 to 100, at most 2 decimals.",
      alreadyReversed: "This posting is already reversed.",
      reverseClosed: "The order is closed; its postings can no longer be reversed.",
      lineClosed: "A material line is closed.",
      stock: "Not enough stock: {{detail}}",
      backflushManual: "Backflush lines are consumed by production reports, not issued by hand.",
      minutesNeedOperation: "Choose the operation the minutes belong to.",
      postPermission: "You do not have permission to post production.",
      stale: "Someone else changed this order meanwhile. Close the editor and open it again.",
      notManufactured: "Only active manufactured items can be produced.",
      serialsRequired: "Enter the serial numbers of the units.",
      serialsCount: "The number of serial numbers must equal the quantity.",
      serialsNeedGood: "Serial numbers belong to the good quantity; scrap takes none.",
      noBom: "No approved BOM of this item is valid on the start date.",
      bomVersion: "The BOM must be an approved version of this item.",
      routingVersion: "The routing must be an approved version of this item in this company.",
      qty: "The quantity must be above zero.",
      dates: "The due date cannot be before the start date.",
      releasedLocked: "After release only the due date and the note can change.",
      frozen: "This production order can no longer be changed.",
      cannotRelease: "Only planned orders can be released.",
      cannotCancel: "Only planned or released orders without postings can be cancelled.",
      cannotComplete: "Only released or in-progress orders can be completed.",
      cannotClose: "Only completed orders can be closed.",
      noMaterials: "The BOM has no components for this order.",
      permission: "You do not have permission to manage production orders.",
    },
  },
  dash: {
    asOf: "{{date}} · updated {{time}}",
    refresh: "Refresh",
    error: "The dashboard could not be loaded.",
    retry: "Try again",
    tiles: "Open work",
    ahead: "Next 14 days",
    overdue: "Overdue",
    today: "Today",
    attention: "Act on first",
    allClear: "Nothing waiting. All clear.",
    trends: "Trends",
    last30: "Last 30 days, 7-day average",
    vsPrevious: "vs the previous 30 days",
    series: {
      outbound: "Shipments",
      inbound: "Receipts",
      due: "Orders due",
      purchase: "Purchases to release",
      production: "Production to release",
      ends: "Activities ending",
    },
    flow: {
      so_new: "Sales orders released",
      shipped: "Lines shipped",
      po_new: "Purchase orders released",
      received: "Lines received",
      transferred: "Transfers",
      produced: "Production receipts",
      consumed: "Material issued",
      ncr_new: "New NCRs",
      adjusted: "Adjustments and counts",
    },
    rate: {
      otd: "On-time delivery",
      supplier_otd: "Supplier on time",
      schedule: "Schedule adherence",
      fpy: "First-pass quality",
    },
    sales: {
      title: "Sales dashboard",
      intro: "Orders to ship, late and short, and the shipping load ahead.",
      aheadHint: "Open sales orders by their next requested date",
      tile: {
        so_late: "Late to ship",
        so_short: "Short of stock",
        so_due_week: "Due this week",
        so_open: "Open orders",
        so_draft: "Drafts",
        shipped_today: "Shipped today",
      },
      kind: { late: "Late", short: "Short" },
    },
    procurement: {
      title: "Purchasing dashboard",
      intro: "Orders overdue from suppliers, drafts to release and the receiving load ahead.",
      aheadHint: "Open purchase orders by their next promised date",
      tile: {
        po_late: "Overdue from suppliers",
        po_due_week: "Due this week",
        po_draft: "Drafts to release",
        po_open: "Open orders",
        suggestions: "Agent suggestions",
        received_today: "Received today",
      },
      kind: { late: "Overdue", draft: "Draft" },
    },
    warehousing: {
      title: "Warehouse dashboard",
      intro: "Inbound and outbound work due, open counts and stock on hold.",
      aheadHint: "Receipts and shipments by date",
      tile: {
        receipts_due: "Receipts due",
        shipments_due: "Shipments due",
        counts_open: "Counts open",
        transfers_draft: "Draft transfers",
        adjustments_draft: "Draft adjustments",
        lots_on_hold: "Lots on hold",
        locations_blocked: "Blocked locations",
        postings_today: "Postings today",
      },
      kind: { receipt: "Receipt", shipment: "Shipment", count: "Count" },
    },
    manufacturing: {
      title: "Production dashboard",
      intro: "Orders short of material or late, and the production due ahead.",
      aheadHint: "Open production orders by due date",
      tile: {
        mo_short: "Short of material",
        mo_late: "Past due",
        mo_planned: "Planned",
        mo_released: "Released",
        mo_in_progress: "In progress",
        completed_today: "Completed today",
      },
      kind: { short: "Short", late: "Late" },
    },
    planning: {
      title: "Planning dashboard",
      intro: "Components short, orders to release and the result of the latest MRP run.",
      aheadHint: "Planned orders by release (start) date",
      lastRun: "Latest MRP run {{time}}",
      runItems_one: "{{count}} item planned",
      runItems_other: "{{count}} items planned",
      runItems_few: "{{count}} items planned",
      runItems_many: "{{count}} items planned",
      runPlanned_one: "{{count}} planned order",
      runPlanned_other: "{{count}} planned orders",
      runPlanned_few: "{{count}} planned orders",
      runPlanned_many: "{{count}} planned orders",
      runExceptions_one: "{{count}} exception",
      runExceptions_other: "{{count}} exceptions",
      runExceptions_few: "{{count}} exceptions",
      runExceptions_many: "{{count}} exceptions",
      noRun: "No MRP run yet. Run MRP to see coverage and planned orders.",
      openMrp: "Open MRP",
      tile: {
        critical: "Components short",
        warning: "Short later",
        expedite: "To expedite",
        release_purchase: "Purchases to release",
        release_production: "Production to release",
        suggestions: "Agent suggestions",
      },
      kind: { critical: "Critical", no_stock: "No stock" },
    },
    quality: {
      title: "Quality dashboard",
      intro: "Inspections waiting, nonconformances to decide and lots on hold.",
      aheadHint: "",
      tile: {
        inspections_old: "Waiting over 2 days",
        inspections_open: "Inspections open",
        ncr_to_disposition: "NCRs to decide",
        ncr_to_close: "NCRs to close",
        lots_on_hold: "Lots on hold",
        decided_today: "Decided today",
      },
      kind: { inspection: "Inspection", ncr: "NCR" },
    },
    project: {
      title: "Project dashboard",
      intro: "Late activities, milestones ahead and time booked this week.",
      aheadHint: "Open activities by planned end",
      tile: {
        activities_late: "Activities late",
        milestones_2w: "Milestones in 2 weeks",
        projects_active: "Active projects",
        projects_draft: "Draft projects",
        mo_open: "Project production orders",
        hours_week: "Hours this week",
      },
      kind: { late: "Late" },
    },
  },
  rcv: {
    title: "Receiving",
    intro: "Find the order from the delivery on the dock, receive it, print its labels.",
    window: "Due within",
    windowToday: "Today",
    windowDays_one: "{{count}} day",
    windowDays_other: "{{count}} days",
    windowDays_few: "{{count}} days",
    windowDays_many: "{{count}} days",
    scan: "Scan or search",
    scanPlaceholder: "PO number, supplier reference, supplier or item code",
    queue: "Due in",
    matches: "Matching orders",
    empty: "Nothing due in this window.",
    noMatch: "No order to receive matches “{{term}}”.",
    tooBroad: "Too many suppliers match — type more of the name to search by supplier.",
    more: "More orders — narrow the search or the window.",
    match: { number: "PO number", reference: "Supplier ref.", supplier: "Supplier", item: "Item" },
    overdue_one: "{{count}} day late",
    overdue_other: "{{count}} days late",
    overdue_few: "{{count}} days late",
    overdue_many: "{{count}} days late",
    dueToday: "Due today",
    openLines: "{{open}} of {{total}} lines open",
    partly_one: "{{count}} partly received",
    partly_other: "{{count}} partly received",
    partly_few: "{{count}} partly received",
    partly_many: "{{count}} partly received",
    moreLines_one: "+ {{count}} more line",
    moreLines_other: "+ {{count}} more lines",
    moreLines_few: "+ {{count}} more lines",
    moreLines_many: "+ {{count}} more lines",
    order: "Order to receive",
    pick: "Pick an order, or scan its delivery note and press Enter.",
    openOrder: "Open order",
    noPermission: "You can see the order but not post receipts.",
    notReceivable: "This order is not open for receipts.",
    posted: "{{number}} posted.",
    today: "Received today",
    todayEmpty: "Nothing received yet today.",
    journal: "Receipts journal",
    reversal: "Reversal",
    reversed: "Reversed",
    inspection: "Inspection {{number}}",
    inspectionStatus: { open: "open", in_progress: "in progress", decided: "decided", cancelled: "cancelled" },
  },
  wb: {
    title: "Operator workbench",
    intro: "Take an operation from the work center's queue, clock setup and run, report what you made.",
    workCenter: "Work center",
    noCenters: "No active work centers in this company.",
    queue: "Queue",
    empty: "Nothing waiting at this work center.",
    pick: "Pick an operation from the queue.",
    operation: "Operation",
    op: "op {{seq}}",
    short: "Short",
    activeOperators_one: "{{count}} operator working",
    activeOperators_other: "{{count}} operators working",
    activeOperators_few: "{{count}} operators working",
    activeOperators_many: "{{count}} operators working",
    previousDone: "Previous operation done: {{qty}}",
    done: "Done",
    scrap: "Scrap",
    planned: "Planned",
    minutesPlanned: "setup {{setup}} · run {{run}} min",
    clocked: "My time since report",
    minutes: "{{value}} min",
    clock: "Clock",
    report: "Report",
    good: "Good",
    minutesLabel: "Minutes",
    note: "Note",
    minutesHint: "Empty minutes = the time you clocked (setup + run) since your last report.",
    stopAfter: "Stop the clock after the report",
    reportButton: "Report",
    reported: "{{number}} posted with {{minutes}} min.",
    lastOperation: "Last operation: report the finished goods on the order.",
    openOrder: "Open order",
    materials: "Materials",
    item: "Item",
    required: "Required",
    issued: "Issued",
    method: "Issue",
    issue: { manual: "manual", backflush: "backflush" },
    events: "Recent clock events",
    state: { idle: "Not started", setup: "Setup", run: "Running", pause: "Paused", stop: "Stopped" },
    action: { setup: "Setup", run: "Run", pause: "Pause", stop: "Stop" },
    event: { setup: "setup", run: "run", pause: "pause", stop: "stop", report: "report" },
  },
  cover: {
    notInRun: "Not in the latest MRP run",
    title: "Stock coverage",
    gridLabel: "Component coverage",
    detailLabel: "Item coverage",
    closeDetail: "Close the item",
    filterStatus: "Filter by status",
    allStatuses: "All",
    searchPlaceholder: "Item code (prefix)",
    anyBuyer: "Any buyer",
    procurement: "Bought or made",
    proc: { all: "All", purchase: "Bought", manufacture: "Made" },
    export: "Export",
    week: "W{{n}}",
    days_one: "{{count}} day",
    days_few: "{{count}} days",
    days_many: "{{count}} days",
    days_other: "{{count}} days",
    leadTime_one: "lead time {{count}} day",
    leadTime_few: "lead time {{count}} days",
    leadTime_many: "lead time {{count}} days",
    leadTime_other: "lead time {{count}} days",
    shortFrom: "short from {{date}}",
    totalDemand: "Demand, 26 weeks",
    weekly: "Week by week",
    weeklyHint: "Demand, firm supply (purchase and production orders), planned orders of the MRP run and the projected stock at the end of each week.",
    cellTitle: "W{{week}} · demand {{demand}} · supply {{supply}} · planned {{planned}} · stock {{stock}}",
    loaded_one: "{{count}} item",
    loaded_few: "{{count}} items",
    loaded_many: "{{count}} items",
    loaded_other: "{{count}} items",
    endOfList: "end of list",
    emptyTitle: "No items to show",
    emptyBody: "Coverage comes from the latest MRP run. Run MRP, or clear the filters.",
    col: {
      item: "Item",
      status: "Status",
      supplier: "Supplier",
      buyer: "Buyer",
      onHand: "On hand",
      perDay: "Per day",
      cover: "Cover (days)",
      transit: "On order (PO)",
      nextSupply: "Next delivery",
    },
    series: { demand: "Demand", supply: "Supply", planned: "Planned", balance: "Stock" },
    status: {
      critical: "Critical",
      no_stock: "No stock",
      warning: "Warning",
      watch: "Watch",
      overstock: "Overstock",
      ok: "OK",
      no_demand: "No demand",
    },
    legend: {
      short: "short",
      low: "below safety stock",
      excess: "overstock",
      cell: "Cells: stock at the end of the week (with planned orders); small: −demand +supply.",
    },
  },
  massRelease: {
    open: "Mass release",
    eyebrow: "Planned orders",
    title: "Mass release",
    intro: "Convert every planned order due by the horizon and release the new purchase and production orders. What cannot be released stays a draft with the reason.",
    dueUntil: "Due by",
    kind: "Kind",
    supplier: "Supplier",
    buyer: "Buyer",
    anyBuyer: "Any buyer",
    firmedOnly: "Firmed only",
    releaseDocs: "Release the new documents",
    preview: "Preview",
    run_one: "Release {{count}} planned order",
    run_other: "Release {{count}} planned orders",
    run_few: "Release {{count}} planned orders",
    run_many: "Release {{count}} planned orders",
    runConvert_one: "Convert {{count}} planned order",
    runConvert_other: "Convert {{count}} planned orders",
    runConvert_few: "Convert {{count}} planned orders",
    runConvert_many: "Convert {{count}} planned orders",
    stop: "Stop after this batch",
    previewTitle: "Preview",
    purchaseCount_one: "{{count}} planned purchase",
    purchaseCount_other: "{{count}} planned purchases",
    purchaseCount_few: "{{count}} planned purchases",
    purchaseCount_many: "{{count}} planned purchases",
    productionCount_one: "{{count}} planned production order",
    productionCount_other: "{{count}} planned production orders",
    productionCount_few: "{{count}} planned production orders",
    productionCount_many: "{{count}} planned production orders",
    subcontractCount_one: "{{count}} planned subcontracting order",
    subcontractCount_other: "{{count}} planned subcontracting orders",
    subcontractCount_few: "{{count}} planned subcontracting orders",
    subcontractCount_many: "{{count}} planned subcontracting orders",
    capped: "First 1000 shown — narrow the horizon to see all.",
    noSupplier_one: "{{count}} planned purchase has no supplier and is skipped — set the default supplier in planning parameters.",
    noSupplier_other: "{{count}} planned purchases have no supplier and are skipped — set the default supplier in planning parameters.",
    noSupplier_few: "{{count}} planned purchases have no supplier and are skipped — set the default supplier in planning parameters.",
    noSupplier_many: "{{count}} planned purchases have no supplier and are skipped — set the default supplier in planning parameters.",
    noPoRight: "You may not create or release purchase orders: planned purchases will fail.",
    noMoRight: "You may not create or release production orders: planned production orders will fail.",
    orders_one: "{{count}} line",
    orders_other: "{{count}} lines",
    orders_few: "{{count}} lines",
    orders_many: "{{count}} lines",
    noPrice_one: "{{count}} without price",
    noPrice_other: "{{count}} without price",
    noPrice_few: "{{count}} without price",
    noPrice_many: "{{count}} without price",
    moreSuppliers: "+ {{count}} more suppliers",
    nothing: "Nothing to convert with these filters.",
    resultTitle: "Result",
    summary: "Batches: {{batches}} · released {{released}} · drafts {{drafts}} · failed {{failed}}",
    remaining_one: "{{count}} planned order not done yet — run again.",
    remaining_other: "{{count}} planned orders not done yet — run again.",
    remaining_few: "{{count}} planned orders not done yet — run again.",
    remaining_many: "{{count}} planned orders not done yet — run again.",
    documents: "New documents",
    failedTitle: "Not converted",
  },
  plan: {
    subcontracting: "Subcontracting",
    subcontractWarehouseAt: "material at {{code}}",
    subcontracted: "Made by a subcontractor",
    subcontractedHint: "The supplier makes it from our material (BOM), sent to its subcontractor warehouse; MRP plans subcontracting orders.",
    subcontractor: "Subcontractor",
    subcontractWarehouse: "Subcontractor warehouse",
    subcontractWarehouseHint: "Where the material goes and is consumed.",
    schedule: {
      button: "Schedule",
      title: "Scheduled MRP",
      hint: "MRP runs by itself at this time on the chosen days (company time zone {{tz}}).",
      enabled: "Run MRP automatically",
      time: "Time",
      days: "Number of days",
      horizon: "Horizon (days)",
      save: "Save schedule",
      cancel: "Cancel",
      saved: "Schedule saved",
      off: "No schedule",
      next: "Next scheduled run {{when}}",
      byScheduler: "by schedule",
      dayShort: { "1": "Mon", "2": "Tue", "3": "Wed", "4": "Thu", "5": "Fri", "6": "Sat", "7": "Sun" },
    },
    buyer: "Buyer",
    colReadiness: "Readiness",
    colMaterialDate: "Material date",
    readiness: {
      ready: "Ready",
      covered: "Covered",
      waiting: "Waiting",
      late: "Late",
      short: "Short",
      no_bom: "No BOM",
    },
    readinessHint: {
      ready: "Every component is on stock.",
      covered: "Covered by stock and open orders arriving by the start date.",
      waiting: "Some components wait for other planned orders — convert those first.",
      late: "An open order covering a component arrives after the start date.",
      short: "Part of a component is not covered within the planning horizon.",
      no_bom: "No approved BOM is valid on the start date.",
    },
    readinessNone: "Readiness is calculated by the next MRP run.",
    shortComponentsHint: "Components not on stock",
    components: "Components",
    materialDate: "Material available {{date}}.",
    asOfRun: "as of MRP {{when}}",
    noComponents: "No components.",
    noPegging: "No pegging yet — run MRP.",
    subComponents: "Components of {{item}}",
    subOrder: "Planned production {{item}} · {{qty}} {{uom}} · start {{start}}",
    peg: {
      stock: "Stock {{qty}}",
      short: "Short {{qty}}",
      late: "late",
      plannedPurchase: "Planned purchase {{qty}}",
      plannedProduction: "Planned production {{qty}}",
      plannedSubcontract: "Planned subcontracting {{qty}}",
      notPlanned: "Not planned by MRP",
      notPlannedHint: "Planning method none — not part of the readiness; use Check now.",
    },
    checkNow: "Check now",
    checkNowHint: "Free stock now, for this order alone",
    liveReady: "All components are free on stock now.",
    liveShort: "Not every component is free on stock now.",
    liveNoBom: "No approved BOM is valid on the start date.",
    colComponent: "Component",
    colRequired: "Required",
    colFree: "Free",
    colShort: "Short",
    convertWarnTitle: "Components are not covered ({{readiness}})",
    convertWarnBody: "The production order is created anyway; its material lines show the shortage at release.",
    bulkConvert: "Convert",
    bulkNoneOpen: "None of the checked suggestions is open.",
    bulkConvertConfirm_one: "Convert {{count}} planned order into a purchase or production order? Suggestions that are not ready are converted too.",
    bulkConvertConfirm_few: "Convert {{count}} planned orders into purchase and production orders? Suggestions that are not ready are converted too.",
    bulkConvertConfirm_many: "Convert {{count}} planned orders into purchase and production orders? Suggestions that are not ready are converted too.",
    bulkConvertConfirm_other: "Convert {{count}} planned orders into purchase and production orders? Suggestions that are not ready are converted too.",
    bulkDeleteConfirm_one: "Delete {{count}} planned order? The next MRP run may suggest it again.",
    bulkDeleteConfirm_few: "Delete {{count}} planned orders? The next MRP run may suggest them again.",
    bulkDeleteConfirm_many: "Delete {{count}} planned orders? The next MRP run may suggest them again.",
    bulkDeleteConfirm_other: "Delete {{count}} planned orders? The next MRP run may suggest them again.",
    convertAnyway: "Convert anyway",
    projectSegment: "Project {{code}} — own stock and supply only",
    freeSegment: "Free stock",
    mrpTitle: "MRP",
    plannedDetailLabel: "Planned order details",
    selectPrompt: "Select a planned order",
    exceptionListLabel: "MRP exceptions",
    exceptionsEmptyTitle: "No exceptions",
    exceptionsEmptyBody: "The latest MRP run found nothing to act on.",
    allExceptions: "All",
    colException: "Exception",
    colSource: "Document",
    colNeed: "Needed",
    colSupply: "Arrives",
    openCoverage: "Open stock coverage",
    convertedTo: "Converted: {{number}}",
    alreadyConverted: "This planned order is already converted into a document.",
    firm: "Firm",
    changeFirmed: "Change",
    firmHint: "Firmed orders stay through MRP runs and count as supply.",
    delete: "Delete",
    confirmDelete: "Delete this planned order? The next MRP run may suggest it again.",
    convertPurchase: "Convert to purchase order",
    convertProduction: "Convert to production order",
    editParameters: "Change planning parameters",
    plannedTitle: "Planned orders",
    plannedListLabel: "Planned order list",
    plannedEmptyTitle: "No planned orders",
    plannedEmptyBody: "Run MRP to get purchase and production suggestions.",
    coverageTitle: "Stock coverage",
    item: "Item",
    pickItem: "Choose an item to see its projected stock.",
    runMrp: "Run MRP",
    runQueued: "MRP run queued…",
    runRunning: "MRP is running…",
    runFailed: "The last MRP run failed: {{error}}",
    stale: "Schedule changed {{when}} after the last MRP run — run MRP to refresh the plan",
    lastRun: "Last run {{when}} · {{orders}} planned orders · {{exceptions}} exceptions",
    noRun: "MRP has not run yet.",
    noRunYet: "No completed MRP run yet. Run MRP to see the projection.",
    noEvents: "No demand, supply or stock for this item in the plan.",
    colKind: "Type",
    colStart: "Start",
    colDue: "Due",
    colSupplier: "Supplier / warehouse",
    colDate: "Date",
    colEvent: "Event",
    colBalance: "Balance",
    allKinds: "All types",
    noSupplier: "No supplier",
    startInPast: "Start date in the past",
    kinds: { purchase: "Purchase", production: "Production", subcontract: "Subcontracting" },
    tabs: { purchase: "Planned purchase", production: "Planned production", subcontract: "Planned subcontracting" },
    statuses: { planned: "Planned", firmed: "Firmed", converted: "Converted" },
    projection: "Projection",
    asOf: "as of {{date}}",
    unit: "Quantities in {{uom}}.",
    lineKinds: { on_hand: "On hand", demand: "Demand", supply: "Supply", planned: "Planned order" },
    sources: {
      sales_order: "Sales order",
      purchase_order: "Purchase order",
      production_order: "Production order",
      planned_order: "Planned order",
    },
    exceptions: "Exceptions",
    exceptionKinds: {
      expedite: "Expedite",
      postpone: "Postpone",
      excess: "Not needed",
      start_in_past: "Start date in the past",
      demand_in_past: "Demand in the past",
      below_safety: "Below safety stock",
      no_bom: "No valid BOM",
    },
    needOn: "needed {{date}}",
    supplyOn: "arrives {{date}}",
    parameters: "Planning",
    method: "Method",
    methods: { mrp: "MRP", none: "Not planned" },
    defaults: "defaults",
    leadTime: "Lead time",
    leadTimeDays: "Lead time (days)",
    leadTimeHint: "Empty = supplier's lead time (purchase) or 0.",
    days_one: "{{count}} day",
    days_few: "{{count}} days",
    days_many: "{{count}} days",
    days_other: "{{count}} days",
    fromSupplier: "from the supplier",
    supplier: "Default supplier",
    safetyStock: "Safety stock",
    lotSizing: "Lot size",
    lotForLot: "Lot-for-lot",
    fixedQty: "fixed {{qty}}",
    minMultiple: "min {{min}} · multiple {{mult}}",
    fixedOrderQty: "Fixed order qty",
    minOrderQty: "Minimum order qty",
    orderMultiple: "Order multiple",
    receiptWarehouse: "Receipt warehouse",
    mainWarehouse: "Main warehouse",
    lotHint: "Fixed quantity wins; otherwise the shortage is raised to the minimum and rounded up to the multiple. 0 = not used.",
    err: {
      weekdays: "Choose at least one day.",
      horizon: "The horizon is 7 to 730 days.",
      stale: "The plan changed meanwhile (MRP ran or someone acted). Reload the list.",
      tooMany: "Convert at most 50 production orders at a time.",
      running: "MRP is running for this company; try again in a moment.",
      noSupplier: "Set the default supplier in the item's planning parameters first.",
      converted: "This planned order is already converted.",
      qty: "The quantity must be above zero and fit the unit.",
      actionPermission: "You do not have permission to manage planned orders.",
      decimals: "A quantity has more decimals than the unit allows.",
      negative: "Quantities are numbers, zero or more.",
      leadTime: "Lead time is 0 to 365 days.",
      supplier: "The default supplier must be an active supplier.",
      notNettable: "The receipt warehouse must count in MRP.",
      permission: "You do not have permission to run planning.",
    },
  },
  companies: {
    sampleTitle: "Sample data",
    sampleHint:
      "Creates a small seat-manufacturing data set in this company to try the modules: warehouses and locations, items, suppliers and customers, BOMs and routings, opening stock, purchase, sales and production orders. Every code starts with DEMO-.",
    sampleLoad: "Load sample data",
    sampleConfirm: "Load into {{company}}",
    sampleDone: "Sample data loaded: {{items}} items, {{partners}} business partners and their documents.",
    sampleAlready: "Sample data is already loaded in this organization.",
    sampleDenied: "Only an administrator with item, partner, warehouse, purchasing, sales and production rights can load sample data.",
    sampleNotEmpty: "Sample data can be loaded only into a company that has no documents or stock movements yet.",
    sampleWarning: "This cannot be undone: the documents take the first numbers of this company's series and stock movements are permanent.",
    sampleFailed: "Sample data could not be loaded. Nothing was changed.",
    title: "Companies",
    listLabel: "Company list",
    detailLabel: "Company details",
    selectPrompt: "Select a company",
    selectPromptHint: "Its letterhead is printed on purchase orders, delivery notes and other documents.",
    colCode: "Code",
    colName: "Name",
    colCurrency: "Currency",
    inactive: "Inactive",
    letterhead: "Letterhead on documents",
    noLetterhead: "No letterhead yet.",
    name: "Name",
    legalName: "Legal name",
    taxId: "Tax ID",
    address1: "Address",
    address2: "Address, line 2",
    postalCode: "Postal code",
    city: "City",
    email: "E-mail",
    phone: "Phone",
    saved: "Saved.",
    errField: "Check this field.",
    errEmail: "Enter a valid e-mail address.",
    errSave: "The letterhead could not be saved. Check the fields and your permissions.",
    logo: "Logo",
    noLogo: "No logo",
    logoLoading: "Loading…",
    logoAlt: "Logo of {{name}}",
    uploadLogo: "Upload logo",
    replaceLogo: "Replace logo",
    removeLogo: "Remove",
    logoHint: "PNG or JPEG, up to 512 KB. Printed in the top left corner of documents.",
    errLogo: "The logo could not be uploaded.",
    errLogoType: "Use a PNG or JPEG file.",
    errLogoSize: "The file is larger than 512 KB.",
  },
  atp: {
    title: "Availability (next 90 days)",
    availableNow: "Available now",
    reserved: "Reserved",
    atpNow: "Available to promise",
    noEvents: "No open supply or demand in the next 90 days.",
    colDate: "Date",
    colDocument: "Document",
    colQty: "Qty",
    colProjected: "Projected",
    kind: { purchase: "Purchase", production: "Production", sales: "Sales", material: "Material" },
    showAll: "Show all {{count}}",
    showLess: "Show less",
    onDate: "Available to promise on {{date}}: {{qty}} {{uom}}",
    earliest: "earliest {{date}}",
    notInHorizon: "not within 90 days",
  },
  lots: {
    title: "Lots",
    listLabel: "Lots",
    detailLabel: "Lot",
    colLot: "Lot",
    colItem: "Item",
    colDescription: "Description",
    colExpiry: "Use by",
    colStatus: "Status",
    colOnHand: "On hand",
    colUom: "Unit",
    blocked: "Blocked",
    expired: "Expired",
    expiry: "Use by",
    produced: "Produced",
    supplierLot: "Supplier lot",
    onHand: "On hand (this company)",
    block: "Block lot",
    release: "Release lot",
    errBlock: "The lot could not be changed (permission needed: inventory.lot.manage).",
    stock: "Stock by location",
    noStock: "No stock of this lot in this company.",
    trace: "Traceability",
    whereFrom: "Where from",
    whereUsed: "Where used",
    noOrigin: "No receipt or production order created this lot (e.g. opening balance).",
    noUse: "This lot has not been used in production or shipped.",
    nothingYet: "Nothing reported on this order yet",
    viaOrder: "Production order {{number}}",
    viaReceipt: "Receipt {{number}} · PO {{order}}",
    viaShipment: "Shipment {{number}} · SO {{order}}",
    viaReturn: "Return receipt {{number}} · RMA {{order}}",
    viaSupplierReturn: "Return to supplier {{number}} · {{order}}",
    shownAbove: "shown above",
    truncated: "The trace is long: the first 500 links / 6 levels are shown.",
    errTrace: "The trace could not be loaded.",
    filterExpiring: "Expiring in 30 days",
    filterBlocked: "Blocked only",
    searchPlaceholder: "Lot code starts with…",
    emptyTitle: "No lots",
    emptyBody: "Lots are created by receipts, production reports and adjustments of lot-controlled items.",
    selectPrompt: "Select a lot",
    selectPromptHint: "See its stock and trace it back to the receipt or forward to the customer.",
  },
  quality: {
    spot: {
      button: "Block",
      buttonLabel: "Block {{where}} for inspection",
      title: "Block for inspection",
      hint: "Spot check at {{where}}: the stock goes to quarantine (or its lot is blocked) and quality decides.",
      qty: "Quantity",
      reason: "Reason",
      reasonPlaceholder: "e.g. damaged packaging, wrong label",
      submit: "Block",
      cancel: "Cancel",
      doneMoved: "{{number}}: moved to quarantine and waiting for a decision.",
      doneBlocked: "{{number}}: the lot is blocked and waiting for a decision.",
      open: "Open inspection",
      shortfall: "Reservations are no longer covered — {{list}}. Check the sales and production orders of this item.",
      shortfallItem: "{{reserved}} reserved, {{reservable}} reservable",
      close: "Close",
      err: {
        reason: "Give the reason for blocking.",
        noQuarantine: "This warehouse has no quarantine location: stock without a lot cannot be blocked. Set one on the warehouse.",
        inQuarantine: "This stock is already in quarantine.",
      },
    },
    serials: "Serial numbers",
    ncr: {
      title: "Nonconformance reports",
      new: "New nonconformance",
      listLabel: "Nonconformance reports",
      detailLabel: "Nonconformance report",
      selectPrompt: "Select a nonconformance report",
      emptyTitle: "No nonconformance reports",
      emptyBody: "Rejected inspections create them; report a problem with stock on hand by hand.",
      searchPlaceholder: "NCR number, item code or description",
      inspection: "Inspection",
      manual: "Reported by hand",
      reported: "Reported",
      closed: "Closed",
      reinspection: "Re-inspection",
      reworkCreated: "Closed; re-inspection {{number}} opened.",
      returnCreated: "Return to the supplier {{number}} is released; its shipment closes this report.",
      supplierReturn: "Return to supplier",
      returnPending: "Waiting for the return shipment.",
      stock: "Nonconforming stock",
      description: "What is wrong",
      create: "Report nonconformance",
      dispositionTitle: "Disposition",
      setDisposition: "Set disposition",
      execute: "Execute disposition",
      cancel: "Cancel report",
      target: "Move the stock to",
      status: { open: "Open", dispositioned: "Dispositioned", closed: "Closed", cancelled: "Cancelled" },
      filter: { open: "Open", closed: "Closed", cancelled: "Cancelled" },
      disposition: { use_as_is: "Use as is", rework: "Rework", scrap: "Scrap", return_to_supplier: "Return to supplier" },
      dispositionHint: {
        use_as_is: "The stock is moved to the chosen location and released.",
        rework: "The stock goes to the rework location; a re-inspection order is opened there and the lot stays held.",
        scrap: "The stock is written off (adjustment out, reason SCRAP).",
        return_to_supplier: "The stock is booked out to the supplier (adjustment out, reason RTV).",
      },
      err: {
        noDisposition: "Set the disposition first.",
        target: "Choose an active, receivable location of the warehouse.",
        supplier: "Return to supplier is possible only for received goods.",
        cancelFromInspection: "A report from an inspection needs a disposition; it cannot be cancelled.",
        reworkPlan: "Rework needs an on-demand inspection plan of the item for the re-inspection.",
        final: "This report is already closed or cancelled.",
        description: "Describe what is wrong.",
        permission: "You need the quality.ncr.manage permission.",
      },
    },
    lotStatus: { released: "Released", in_inspection: "In inspection", rejected: "Rejected" },
    ins: {
      rejectUnits: "Rejected units",
      rejectedCount: "{{rejected}} rejected, {{accepted}} accepted",
      title: "Inspection orders",
      new: "New inspection",
      listLabel: "Inspection orders",
      detailLabel: "Inspection order",
      selectPrompt: "Select an inspection order",
      emptyTitle: "No inspection orders",
      emptyBody: "Receipts and production reports of items with an inspection plan create them; or start one on demand.",
      searchPlaceholder: "QI number, item code or description",
      colNumber: "Number",
      qty: "Quantity",
      lot: "Lot",
      source: "Source",
      where: "Warehouse · location",
      closed: "Closed",
      decided: "Decision",
      acceptedRejected: "{{accepted}} accepted, {{rejected}} rejected {{uom}}",
      results: "Results",
      spec: "Specification",
      value: "Value",
      result: "Result",
      ok: "Conforming",
      nok: "Not conforming",
      nokCount_one: "{{count}} not conforming",
      nokCount_few: "{{count}} not conforming",
      nokCount_many: "{{count}} not conforming",
      nokCount_other: "{{count}} not conforming",
      pass: "Pass",
      fail: "Fail",
      saveResults: "Save results",
      decisionTitle: "Decision",
      decide: "Record decision",
      cancel: "Cancel inspection",
      putaway: "Put accepted stock to",
      acceptedQty: "Accepted quantity ({{uom}})",
      missing_one: "{{count}} required result is still missing.",
      missing_few: "{{count}} required results are still missing.",
      missing_many: "{{count}} required results are still missing.",
      missing_other: "{{count}} required results are still missing.",
      saveFirst: "Save the results before deciding.",
      manualHint: "Inspect stock on hand: it stays where it is; its lot is held until the decision. The item needs an on-demand inspection plan.",
      stock: "Stock to inspect",
      noStock: "No stock of this item in the active company.",
      create: "Start inspection",
      status: { open: "Open", in_progress: "In progress", decided: "Decided", cancelled: "Cancelled" },
      decision: { accept: "Accepted", reject: "Rejected", partial: "Partly accepted" },
      filter: { open: "Open", decided: "Decided", cancelled: "Cancelled" },
      sources: { purchase_receipt: "Purchase receipt", production_posting: "Production report", manual: "On demand", sales_return_receipt: "Customer return", purchase_return: "Returns to suppliers", purchase_return_shipment: "Return shipments" },
      err: {
        partialSerials: "Tick the rejected units: at least one, not all.",
        required: "Enter the required results first: {{codes}}.",
        partial: "A partial decision accepts more than zero and less than the whole quantity.",
        cancelResults: "Results were entered; record a decision instead.",
        final: "This inspection is already decided or cancelled.",
        noPlan: "The item has no active on-demand inspection plan (Quality → Inspection plans).",
        onHand: "Only {{qty}} on hand there.",
        lot: "Choose the lot to inspect.",
        putaway: "Choose a location of the inspection's warehouse.",
        counted: "The location is being counted; try again after the count.",
        permission: "You need the quality.inspection.decide permission.",
        number: "{{code}}: enter a number.",
        pickStock: "Choose an item and the stock to inspect.",
        qty: "Enter a quantity above zero.",
      },
    },
    kinds: { measure: "Measured", attribute: "Pass / fail", text: "Text" },
    triggers: { purchase_receipt: "Purchase receipt", production_report: "Production report", manual: "On demand", spot_check: "Spot check" },
    char: {
      title: "Characteristics",
      new: "New characteristic",
      code: "Code",
      codeHint: "Letters, digits, . _ - (up to 20). Cannot change later.",
      name: "Name",
      kind: "Kind",
      kindFixed: "The kind cannot change (plan limits depend on it).",
      uom: "Unit",
      decimals: "Decimals",
      active: "Active",
      detailLabel: "Characteristic",
      selectPrompt: "Select a characteristic",
      emptyTitle: "No characteristics yet",
      emptyBody: "Add what you inspect (length, weld check, remarks) or import them from Excel.",
    },
    plan: {
      title: "Inspection plans",
      new: "New plan",
      listLabel: "Inspection plans",
      detailLabel: "Inspection plan",
      selectPrompt: "Select an inspection plan",
      emptyTitle: "No inspection plans",
      emptyBody: "A plan says what to check on an item at receipt, at production report or on demand.",
      searchPlaceholder: "Item code or description",
      item: "Item",
      trigger: "Trigger",
      supplier: "Supplier",
      supplierOptional: "Supplier (optional)",
      anySupplier: "Any supplier",
      sampleSize: "Sample size",
      sampleHint: "Units checked per inspection ({{uom}}); empty = the whole quantity.",
      all: "All",
      colLines: "Checks",
      revision: "Revision",
      note: "Note",
      active: "Active",
      activeFilter: "Status",
      activeOnly: "Active",
      inactiveOnly: "Inactive",
      lines: "Characteristics to check",
      seq: "Seq.",
      seqOf: "Sequence of line {{n}}",
      characteristic: "Characteristic",
      characteristicOf: "Characteristic of line {{n}}",
      lower: "Lower limit",
      target: "Target",
      upper: "Upper limit",
      required: "Required",
      addLine: "Add characteristic",
      noCharacteristics: "Create characteristics first (Quality → Characteristics).",
    },
    err: {
      serialTwice: "A serial number appears twice; name each unit once.",
      serialBusy: "One of the units is already on an open inspection or nonconformance.",
      serialCount: "Give one serial number per unit.",
      serialStock: "One of the serial numbers is not in stock at that location.",
      notSerial: "This item is not serial-controlled; leave the serial numbers empty.",
      line: "Line {{line}}: {{message}}",
      seq: "a sequence 1–999999 is required",
      seqTwice: "the sequence appears twice",
      characteristic: "choose a characteristic",
      inactive: "the characteristic is inactive",
      characteristicTwice: "the characteristic appears twice",
      limitsMeasure: "limits are allowed only for measured characteristics",
      limitsOrder: "the lower limit is above the upper limit",
      target: "the target is outside the limits",
      number: "enter a number",
      stale: "Someone else changed this plan meanwhile. Reload it and try again.",
      duplicate: "This item already has a plan for this trigger and supplier; edit that plan.",
      supplierTrigger: "Only receipt plans can be supplier-specific.",
      supplier: "Choose a supplier.",
      service: "A service item cannot be inspected.",
      lineCount: "A plan needs between 1 and 200 characteristics.",
      sample: "The sample size must be a number above zero (empty: inspect all).",
      permission: "You need the quality.plan.manage permission.",
      codeTaken: "This code already exists.",
      code: "Code: letters, digits, . _ - (up to 20).",
      name: "Enter a name.",
      decimals: "Decimals: 0 to 6.",
      item: "Choose an item.",
    },
  },
  project: {
    title: "Projects",
    new: "New project",
    create: "Create project",
    listLabel: "Projects",
    detailLabel: "Project",
    selectPrompt: "Select a project",
    emptyTitle: "No projects",
    emptyBody: "Create a project with its work breakdown, or import structures from a sheet.",
    searchPlaceholder: "Project code or name",
    code: "Code",
    codeHint: "Empty = next number from the series (PRJ-…).",
    name: "Name",
    customer: "Customer",
    customerOptional: "Customer (optional)",
    manager: "Project manager",
    dates: "Dates",
    startDate: "Start date",
    endDate: "End date",
    note: "Note",
    noteOptional: "Note (optional)",
    hours: "Hours",
    bookedOfPlanned: "{{booked}} booked of {{planned}} h planned",
    progressLabel: "Progress (earned hours)",
    noPlan: "No planned hours yet",
    locked: "A completed, closed or cancelled project cannot be changed.",
    wbs: "Work breakdown",
    noActivities: "No phases or activities yet.",
    addPhase: "Add phase",
    addActivity: "Add activity",
    milestone: "Milestone",
    colCode: "Code",
    colName: "Name",
    colKind: "Kind",
    colParent: "Phase",
    colStart: "Planned start",
    colEnd: "Planned end",
    colPlanned: "Plan h",
    colBooked: "Booked",
    colDone: "Done",
    colHours: "Hours",
    colHoursBookedPlanned: "Hours",
    codeOf: "Code, row {{n}}",
    nameOf: "Name, row {{n}}",
    kindOf: "Kind, row {{n}}",
    parentOf: "Phase, row {{n}}",
    startOf: "Planned start, row {{n}}",
    endOf: "Planned end, row {{n}}",
    hoursOf: "Planned hours, row {{n}}",
    milestoneOf: "Milestone, row {{n}}",
    doneOf: "Done, row {{n}}",
    kind: { phase: "Phase", activity: "Activity" },
    status: { draft: "Draft", active: "Active", completed: "Completed", closed: "Closed", cancelled: "Cancelled" },
    filter: { open: "Open", draft: "Draft", active: "Active", completed: "Completed", closed: "Closed", cancelled: "Cancelled" },
    action: { activate: "Activate", complete: "Complete", reopen: "Reopen", close: "Close", cancel: "Cancel project" },
    confirm: { close: "Close the project for good?", cancel: "Cancel the project?" },
    tab: { structure: "Structure", time: "Time", orders: "Orders", stock: "Stock" },
    activity: "Activity",
    workDate: "Date",
    who: "Who",
    bookTime: "Book time",
    book: "Book",
    recentTime: "Latest bookings",
    noTime: "No time booked yet.",
    timeOnlyActive: "Time can be booked while the project is active.",
    stock: {
      title: "Project stock",
      hint: "Received or produced for the project, or moved in with a stock transfer. Free demand never takes it; move it back with a transfer to free stock.",
      empty: "The project holds no stock.",
      colWhere: "Warehouse · location",
      free: "Free stock",
      option: "Project {{code}} — {{name}}",
      label: "Stock of",
    },
    pegs: {
      title: "Pegged orders",
      empty: "No orders are pegged to this project yet.",
      onlyActive: "Orders can be pegged while the project is active.",
      form: "Peg an order",
      kind: "Document",
      search: "Find",
      searchPlaceholder: "Order number or item code",
      candidates: "Open orders that can be pegged",
      noCandidates: "No open, untouched orders match.",
      activityOptional: "Activity (optional)",
      peg: "Peg to project",
      pickFirst: "Choose an order first.",
      unpeg: "Unpeg {{document}}",
      colKind: "Kind",
      colDocument: "Document",
      colItem: "Item",
      colDone: "Done / ordered",
      colDue: "Due",
      target: { purchase_order_line: "Purchase order line", sales_order_line: "Sales order line", production_order: "Production order" },
      short: { purchase_order_line: "PO", sales_order_line: "SO", production_order: "MO" },
    },
    progress: {
      title: "Project progress",
      listLabel: "Project progress",
      hint: "Earned hours (done activity = its plan, open = booked up to plan) and the quantities of pegged documents: received, produced and shipped of what is ordered.",
      hours: "Hours earned",
      milestones: "Milestones",
      purchase: "Purchased",
      production: "Produced",
      sales: "Shipped",
      docs: "{{open}} open of {{total}}",
      empty: "No projects match the filter.",
    },
    myTime: {
      title: "My time",
      week: "Week",
      prev: "Previous week",
      next: "Next week",
      thisWeek: "This week",
      total: "{{hours}} h this week",
      project: "Project",
      noActive: "No active projects in this company.",
      noOpenActivities: "All activities of this project are done.",
    },
    err: {
      line: "Row {{line}}: {{message}}",
      company: "Choose a company first.",
      code: "code: letters, digits and . _ - (at most 24).",
      codeTwice: "the code appears twice.",
      name: "a name of 1 to 160 characters is required.",
      parentPhase: "the parent must be a phase.",
      parent: "unknown parent.",
      dates: "the end is before the start.",
      hours: "planned hours: a number with up to two decimals.",
      stale: "Someone else changed this project; reload it and try again.",
      duplicate: "A project with this code already exists in this company.",
      cycle: "The phases form a cycle.",
      booked: "Activity {{code}} has booked time and cannot be removed.",
      noActivities: "Add at least one activity before activating.",
      cancelBooked: "Time is booked on the project; complete it instead of cancelling.",
      notActive: "Time can be booked only on active projects.",
      done: "The activity is done; reopen it to book more time.",
      belowZero: "A correction cannot bring the activity below zero hours.",
      bookHours: "Hours: between −24 and 24, not zero, up to two decimals.",
      workDate: "The date must be within the last year (at most a week ahead).",
      manager: "The manager must be an active member of the organization.",
      activity: "Choose an activity.",
      stockRemains: "Stock of the project remains; transfer it to free stock first.",
      pegNotActive: "Orders can be pegged only to active projects.",
      pegMoved: "Stock has already moved for this order; the peg can change only before receipts, reservations, shipments or reports.",
      pegClosed: "Only open orders can be pegged.",
      pegActivity: "The activity is not part of the project.",
      cancelPegged: "Orders are pegged to the project; unpeg them before cancelling.",
      closePegged: "Pegged orders are still open; finish or close them before closing the project.",
      activityPegged: "Activity {{code}} is pegged to orders and cannot be removed; unpeg them first.",
      transition: "This status change is not allowed now; reload the project.",
      permission: "You don't have permission for this (project.plan.manage / project.time.book).",
    },
  },
  reasons: {
    title: "Adjustment reasons",
    note: "Reasons explain every stock adjustment. Direction limits the sign on adjustment lines. Maintain them by import: codes never change, deactivate instead.",
    colCode: "Code",
    colDescription: "Description",
    colDirection: "Direction",
    colStatus: "Status",
    direction: { in: "In (+)", out: "Out (−)", both: "Both" },
  },
  numbering: {
    title: "Numbering series",
    note: "Prefix tokens: {{YYYY}} and {{YY}} for the year. The next number only moves forward; an inactive series stops new documents of that type.",
    colDocument: "Document",
    colPrefix: "Prefix",
    colPadding: "Digits",
    colNext: "Next number",
    colPreview: "Next document",
    colActive: "Status",
    active: "Active",
    inactive: "Inactive",
    onFirstUse: "Created with the first document",
    errSave: "Could not save (permission core.numbering.manage).",
    noPermission: "You need the core.numbering.read permission to see numbering series.",
    type: { purchase_order: "Purchase orders", purchase_receipt: "Purchase receipts", sales_order: "Sales orders", shipment: "Shipments", production_order: "Production orders", production_posting: "Production postings", stock_adjustment: "Stock adjustments", stock_transfer: "Stock transfers", stock_count: "Cycle counts", inspection_order: "Inspection orders", nonconformance_report: "Nonconformance reports", project: "Projects", engineering_change: "Engineering changes", sales_return: "Customer returns (RMA)", sales_return_receipt: "Return receipts" },
  },
  audit: {
    title: "Audit log",
    listLabel: "Audit log",
    detailLabel: "Change",
    colTime: "When",
    colWhat: "What",
    colTable: "Table",
    colRecord: "Record",
    colAction: "Change",
    colFields: "Fields",
    colWho: "Who",
    action: { insert: "Created", update: "Changed", delete: "Deleted" },
    filterTable: "Table",
    filterUser: "User",
    from: "From",
    to: "To",
    all: "All",
    oneFilter: "Filter by table or by user, one at a time.",
    by: "By {{name}}",
    system: "System",
    unknownUser: "Unknown user",
    changes: "Changes",
    field: "Field",
    before: "Before",
    after: "After",
    noFields: "No business fields changed.",
    noPermission: "You need the core.audit.read permission to see the audit log.",
    emptyTitle: "No changes",
    emptyBody: "No changes match these filters.",
    selectPrompt: "Select a change",
    selectPromptHint: "See every field before and after.",
  },
  journal: {
    colNumber: "Number",
    colPosted: "Posted",
    colDeliveryNote: "Delivery note",
    colLines: "Lines",
    reversal: "Reversal",
    note: "Note",
    detailLabel: "Document",
    selectPrompt: "Select a document",
    searchPlaceholder: "Number starts with…",
    lines_one: "{{count}} line",
    lines_few: "{{count}} lines",
    lines_many: "{{count}} lines",
    lines_other: "{{count}} lines",
    openOrder: "Open order {{number}}",
    receipts: {
      title: "Purchase receipts",
      colOrder: "Purchase order",
      colPartner: "Supplier",
      emptyTitle: "No receipts",
      emptyBody: "Receipts are posted on released purchase orders.",
      selectHint: "See what arrived, where it went, and the lots.",
    },
    shipments: {
      title: "Shipments",
      colOrder: "Sales order",
      colPartner: "Customer",
      emptyTitle: "No shipments",
      emptyBody: "Shipments are posted on released sales orders.",
      selectHint: "See what left, from where, and print the delivery note.",
    },
  },
  picking: {
    colLines: "Lines",
    colShort: "Lines short",
    title: "Picking",
    colDate: "Ship by",
    colOrder: "Sales order",
    colCustomer: "Customer",
    colWarehouse: "Warehouse",
    colState: "Stock",
    partial: "Partly shipped",
    ready: "Reserved",
    short_one: "{{count}} line short",
    short_few: "{{count}} lines short",
    short_many: "{{count}} lines short",
    short_other: "{{count}} lines short",
    due: "Ship by {{date}}",
    detailLabel: "Order to pick",
    selectPrompt: "Select an order",
    selectHint: "Print its pick list in location order, then post the shipment on the order.",
    notInList: "This order is not ready to pick (shipped, closed or not released).",
    shipOnOrder: "Ship on the order",
    emptyTitle: "Nothing to pick",
    emptyBody: "Released sales orders with open lines appear here, the most urgent first.",
  },
  labels: {
    perBox: "Per box",
    boxHint: "One label per box: the quantity is split into boxes of this size, the last one partial. Each box gets a reference <report>/<nnn>.",
    errTooManyBoxes: "Too many boxes at once: use a larger quantity per box (PDF up to 200 labels, ZPL up to 5,000).",
    button: "Label",
    copies: "Copies",
    packQty: "Qty per pack",
    hint: "PDF: 100 × 50 mm for any printer. ZPL: a file for Zebra printers.",
    errTooMany: "Too many labels at once: PDF up to 200, ZPL up to 5,000.",
    errInput: "Copies 1–50; quantity greater than zero.",
  },
  print: {
    button: "Print (PDF)",
    deliveryNote: "Delivery note (PDF)",
    pickList: "Pick list (PDF)",
    traveller: "Traveller (PDF)",
    preparing: "Preparing PDF…",
    errNotAllowed: "This document cannot be printed (not found or not allowed).",
    errSession: "Your session has expired. Sign in again.",
    errFailed: "The PDF could not be prepared. Try again.",
    errLimit: "Printing is busy or today's limit has been reached. Try again in a moment or tomorrow.",
  },
  import: {
    button: "Import",
    title: "Import from Excel",
    what: "What to import",
    entities: {
      items: "Items",
      item_conversions: "Item unit conversions",
      opening_stock: "Opening stock",
      partners: "Business partners",
      locations: "Locations",
      units: "Units of measure",
      partner_addresses: "Partner addresses",
      sites: "Sites",
      warehouses: "Warehouses",
      work_centers: "Work centers",
      boms: "BOMs (new draft versions)",
      routings: "Routings (new draft versions)",
      item_planning: "Planning parameters",
      purchase_orders: "Purchase orders (drafts)",
      sales_orders: "Sales orders (drafts)",
      stock_transfers: "Stock transfer (draft)",
      count_results: "Counted quantities",
      adjustment_reasons: "Adjustment reasons",
      quality_characteristics: "Quality characteristics",
      inspection_plans: "Inspection plans",
      projects: "Projects (work breakdown)",
      project_time: "Project time",
      production_kits: "Kits (orders and links)",
      engineering_boms: "Engineering BOMs (lines)",
      pboms: "Production BOMs (order structure)",
      engineering_changes: "Engineering changes (items)",
      item_sales: "Item sales data",
      customer_items: "Customer item codes",
      price_list_lines: "Price list lines",
      purchase_price_list_lines: "Purchase price list lines",
      sales_discounts: "Discounts",
      sales_returns: "Customer returns (draft RMAs)",
      purchase_returns: "Returns to suppliers (drafts)",
      item_purchase: "Item purchasing data",
      supplier_items: "Supplier items",
    },
    columns: "Columns (* required for new records)",
    hints: {
      items: "Upsert by code: new codes are created; for existing items only the columns in the file change. Nothing is deleted.",
      item_conversions: "1 from unit = factor × to unit, per item. The same pair updates its factor.",
      opening_stock:
        "Target stock per item, warehouse, location and lot in the active company. Only the differences are posted, as stock adjustments (OPENING up, CORRECTION down). Serial-numbered items: one row per unit with serial_no (quantity 1 or empty); listed units come in, units not in the file stay. Up to 10 000 rows.",
      partners: "Upsert by code: suppliers and customers. Only the columns in the file change; nothing is deleted.",
      locations: "Locations of the active company's warehouses, by warehouse + code. Only the columns in the file change.",
      units: "Upsert by code. The dimension and decimals of a unit in use cannot change. Nothing is deleted.",
      partner_addresses:
        "Addresses by partner, kind, street and city: a matching address is updated, otherwise added. One default per partner and kind; a new default replaces the old one.",
      sites: "Sites of the active company, by code. Only the columns in the file change.",
      warehouses:
        "Warehouses of the active company, by code. The site is set when a warehouse is created; stock and movements block deactivation and type changes. Each new warehouse gets its DEFAULT location.",
      work_centers:
        "Work centers of the active company, by code. Supply warehouse and location are codes of this company; a location needs its warehouse in the same row.",
      boms: "One row per component. Each item in the file becomes one new draft BOM version with its lines in file order; approve it on the BOM screen. An item with any bad line is skipped whole.",
      routings:
        "One row per operation, for the active company. Each item becomes one new draft routing version; approve it on the routing screen. An item with any bad operation is skipped whole.",
      item_planning: "Planning parameters of items in the active company, by item code. Only the columns in the file change.",
      purchase_orders:
        "One row per order line, grouped by order_ref (your source order number). Each order becomes one draft in the active company; release it on the order screen. An order with any bad line is skipped whole; an order_ref imported before is refused. Optional project (+ activity) pegs the line to an active project.",
      stock_transfers:
        "One row per line; the file becomes one draft transfer in the active company (up to 500 lines). An empty location is the warehouse's DEFAULT location; post the transfer from its panel. Optional from_project / to_project move stock out of or into a project (empty = free stock).",
      count_results:
        "Counted quantities for this running count, by warehouse, location (empty = DEFAULT), item and lot. A lot not in the count is added as found. Importing again overwrites the entries.",
      adjustment_reasons: "Upsert by code. A new reason needs a description; direction: in, out or both (default both). Codes cannot change; deactivate instead. The opening-stock import uses OPENING: keep it active.",
      projects:
        "One row per activity; the rows of one project_code form one project in the active company. A new project needs project_name and becomes a draft; for an existing draft or active project the file replaces its work breakdown as a whole (activities with booked time must stay). kind: phase or activity; parent = a phase code. A project with any bad row is skipped whole.",
      engineering_boms:
        "One row per line; the rows of one item_code become ONE new draft revision of that item (refused while the item has a draft). component_code and qty per one item; uom (default the component's base unit), find_no, reference, note, change_no (the same for all rows of an item). An item with any bad row is skipped whole.",
      pboms:
        "One row per level below the item, in the active company; the rows of one item_code become ONE new draft version (refused while the item has a draft). node_item_code = the level that gets its own order; parent_item_code = the item or another level of the file (empty = directly below the item); buffer_days = days it must be due before its parent starts. Every level must be a manufactured component of its parent's approved BOM. An item appears once per production BOM in a file. An item with any bad row is skipped whole.",
      engineering_changes:
        "One row per item; the rows of one eco_ref (your reference) become ONE new draft change in the active company. title, reason and effective_date the same on every row of a ref. Each item gets its next engineering BOM revision (refused when its draft belongs to another change). A ref with any bad row is skipped whole.",
      item_sales:
        "Sales data of items in the active company, by item code: sales unit, list price and currency, minimum and multiple, sellable. Only the columns in the file change.",
      customer_items: "The customer's own code for an item, by customer and item code. A row adds the code or changes it.",
      price_list_lines: "Prices of price lists in the active company, by list code, item, unit (empty = base unit) and quantity from (empty = 0). A row adds the price or changes it; closed lists are refused.",
      purchase_price_list_lines: "Prices of purchase price lists in the active company, by list code, item, unit (empty = base unit) and quantity from (empty = 0). A row adds the price or changes it; closed lists are refused.",
      sales_discounts: "Discount rules of the active company by customer, item (empty = the whole range), from quantity and valid from: a matching rule gets the file's % and valid to, otherwise a rule is added. Overlapping periods are refused.",
      sales_returns: "Draft RMAs of the active company: one per return_ref, a row per order line — the sales order number, the item (and the order line when the item is on several), the quantity in the base unit, the reason (damaged, defective, wrong_item, not_needed, other) and inspect (yes / no). At most what was shipped minus what is already returned; a reference imported before is refused.",
      purchase_returns: "Draft returns to suppliers of the active company: one per return_ref, a row per purchase order line — the PO number, the item (and the order line when the item is on several), the quantity in the base unit and the reason (defective, damaged, wrong_item, surplus, other). At most what was received minus what is already returned; a reference imported before is refused.",
      item_purchase: "Purchasing data of items in the active company, by item code: purchase unit, standard price and currency, purchased. Only the columns in the file change; a new unit without a price clears the standard price.",
      supplier_items: "The approved suppliers of items, by supplier and item code: the supplier's code and description, unit, agreed price and currency, lead time, minimum and multiple. Only the columns in the file change.",
      production_kits:
        "One row per production order; the rows of one kit (the number of an open kit, or the name of a new kit) form the kit in the active company. parent = an order of the same kit (in the file or already in it), offset_days = days the order must be due before the parent starts. An empty parent makes the order a root; members not in the file stay. A kit with any bad row is skipped whole.",
      project_time:
        "One row per booking, booked for you on active projects. Hours between −24 and 24 (negative = correction, not below zero per activity); a done activity cannot take time. Bad rows are reported and skipped; the rest is booked in one go (a booking refused at commit stops the whole file).",
      quality_characteristics: "Upsert by code. A new characteristic needs a name and a kind (measure, attribute, text); the kind cannot change. A unit only for measures.",
      inspection_plans:
        "One row per characteristic; the rows of one item + trigger + supplier form one plan. A plan in the file replaces that plan's characteristics as a whole; empty seq = 10, 20 … in file order. Limits only for measures. A plan with any bad row is skipped whole.",
      sales_orders:
        "One row per order line, grouped by order_ref. Each order becomes one draft in the active company with the customer's ship-to address; a line without a price takes the customer's last price. An order with any bad line is skipped whole; an order_ref imported before is refused. Optional project (+ activity) pegs the line to an active project.",
    },
    template: "Download template",
    chooseFile: "Choose an .xlsx file",
    rules: "First sheet, first row = column names. Up to {{max}} rows. Rows are checked before anything is saved.",
    reading: "Reading the file…",
    staging_one: "Uploading {{count}} row…",
    staging_few: "Uploading {{count}} rows…",
    staging_many: "Uploading {{count}} rows…",
    staging_other: "Uploading {{count}} rows…",
    validating: "Checking the rows…",
    committing: "Importing…",
    summary: "Check result",
    rowsTotal: "Rows",
    rowsErrors: "With errors",
    rowsInsert: "New",
    rowsUpdate: "Changed",
    rowsIncrease: "Increase",
    rowsDecrease: "Decrease",
    rowsLines: "Lines",
    doneVersions: "Imported: {{inserted}} lines, {{skipped}} skipped.",
    doneDrafts: "New drafts: {{documents}}",
    doneStock: "Posted: {{inserted}} increases, {{updated}} decreases, {{skipped}} skipped.",
    doneDocuments: "Adjustments: {{documents}}",
    noChanges: "The stock already matches the file — nothing was posted.",
    errorRows: "Rows with errors",
    errorFile: "Download error file",
    errorColumn: "error",
    row: "Row {{row}}",
    moreErrors_one: "+{{count}} more row with errors (in the error file)",
    moreErrors_few: "+{{count}} more rows with errors (in the error file)",
    moreErrors_many: "+{{count}} more rows with errors (in the error file)",
    moreErrors_other: "+{{count}} more rows with errors (in the error file)",
    skipErrors_one: "Skip the rows with errors and import {{count}} valid row",
    skipErrors_few: "Skip the rows with errors and import {{count}} valid rows",
    skipErrors_many: "Skip the rows with errors and import {{count}} valid rows",
    skipErrors_other: "Skip the rows with errors and import {{count}} valid rows",
    commit_one: "Import {{count}} row",
    commit_few: "Import {{count}} rows",
    commit_many: "Import {{count}} rows",
    commit_other: "Import {{count}} rows",
    done: "Imported: {{inserted}} new, {{updated}} changed, {{skipped}} skipped.",
    another: "Import another file",
    changed: "The data changed since the check. Nothing was imported — review the new result and import again.",
    codes: {
      required: "required",
      invalid: "invalid value",
      too_long: "too long",
      duplicate: "appears more than once in the file",
      unknown: "not found",
      frozen: "cannot change: the item has stock movements",
      conflict: "lot and serial tracking together",
      same: "units must differ",
      reverse: "exists in the other direction",
      service_tracked: "a service cannot be tracked by lot or serial number",
      has_stock: "still holds stock and cannot be deactivated",
      counting: "on a running cycle count",
      in_use: "the unit is in use; its dimension and decimals cannot change",
      site_fixed: "the site of a warehouse cannot change",
      negative_stock: "the warehouse has negative stock",
      has_ledger: "the warehouse has stock movements; its type cannot change",
      active_warehouses: "the site still has active warehouses",
      not_nettable: "a new quarantine or transit warehouse starts not nettable; switch it on after creation",
      not_manufactured: "not a manufactured item",
      too_many_lines: "too many lines for one item",
      header_conflict: "lines of one item or order disagree on a header value",
      self_component: "an item cannot be its own component",
      incomplete: "another line of this item or order has errors",
      not_supplier: "not an active supplier",
      not_customer: "not a customer",
      exists: "already imported in this company",
      serial_whole: "a serial-numbered item is ordered in whole units",
      serial: "serial-numbered items cannot be imported here yet",
      too_large: "the line or order amount is too large",
      other_site: "both warehouses must be on the same site",
      same_location: "source and target are the same location",
      not_in_count: "the location is not part of this count",
      open_orders: "the item is on open orders; serial numbers cannot be switched on",
      inactive: "inactive",
      service: "a service has no stock",
      not_serial_controlled: "the item has no serial numbers",
      serial_qty: "a serial row is one unit: quantity 1 or empty",
      serial_base: "a serial row is one unit in the base unit",
      elsewhere: "the unit is in stock at another location; transfer it there first",
      other_lot: "the unit is in another lot",
      project_stock: "the unit is here as project stock",
      not_lot_controlled: "the item has no lots",
      expiry_mismatch: "conflicting expiry date for this lot",
      no_conversion: "no conversion to the base unit",
      precision: "does not convert exactly into the base unit",
      no_role: "a partner is a supplier, a customer or both",
      default_location: "the DEFAULT location stays active, unblocked, receivable and pickable",
    },
    err: {
      tooMany: "At most {{max}} rows per import of this kind. Split the file.",
      hasErrors: "Some rows have errors: fix them or choose to skip them.",
      noValid: "No valid rows to import.",
      noHeader: "The file has no header row.",
      duplicateHeader: "A column appears twice in the header row.",
      keyColumns: "The file is missing required columns.",
      missingColumns: "Missing columns: {{columns}}.",
      empty: "The file has no data rows.",
      frozen: "An item with stock movements cannot change its unit, type or tracking.",
      permission: "You do not have permission to import this data.",
      cellTooLong: "A cell is longer than 1 000 characters or holds more than a single value.",
      createdMeanwhile: "A record with the same key was created meanwhile. Check the file again.",
      malformed: "The file could not be read row by row. Save it again as .xlsx and retry.",
      fileLimit: "The file is too large for one import of this kind (orders: 200 orders / 5 000 lines; BOMs and routings: 1 000 items; transfers: 500 lines; counts: 1 000 lines). Split the file.",
      countFull: "The count would grow beyond 1 000 lines. Split the count into smaller ranges.",
      countNotRunning: "The count is no longer running.",
      series: "The numbering series for these orders is inactive. Activate it in Settings → Numbering and retry.",
      company: "Choose the company for this import.",
      reason: "The adjustment reason OPENING or CORRECTION is inactive. Activate it in the adjustment reasons and retry.",
    },
  },
  so: {
    title: "Sales orders",
    newOrder: "New order",
    listLabel: "Sales order list",
    detailLabel: "Order details",
    searchPlaceholder: "Order or customer…",
    colOrder: "Order",
    colCustomer: "Customer",
    colStatus: "Status",
    colRequested: "Requested",
    colReserved: "Reserved",
    colShortLines: "Lines short",
    fullyReserved: "Reserved",
    shortCount_one: "{{count}} line short",
    shortCount_few: "{{count}} lines short",
    shortCount_many: "{{count}} lines short",
    shortCount_other: "{{count}} lines short",
    emptyTitle: "No sales orders match",
    pickCustomer: "Search customer…",
    noCustomers: "No active customer matches.",
    customerReference: "Customer reference",
    warehouse: "Ship from",
    requested: "Requested date",
    shipTo: "Ship to",
    shipToDefault: "Customer default",
    lastPrice: "Last price",
    discountPct: "Discount %",
    discountHint: "Discount on the list price (customer rules: Sales → Discounts). Changing it recomputes the net price.",
    defaultsHint: "Empty price: the price list, else the customer's last price. Empty date: the order date.",
    shippedSoFar: "{{qty}} shipped; item cannot change",
    release: "Release and reserve",
    saveAndRelease: "Save, release and reserve",
    reserveAgain: "Reserve again",
    shortLines_one: "{{count}} line is not fully reserved.",
    shortLines_few: "{{count}} lines are not fully reserved.",
    shortLines_many: "{{count}} lines are not fully reserved.",
    shortLines_other: "{{count}} lines are not fully reserved.",
    reservedOf: "{{reserved}} reserved · {{shipped}} shipped",
    availableNow: "{{qty}} free in the warehouse",
    missing: "{{qty}} short",
    lineProgress: "{{item}}: {{shipped}} shipped, {{reserved}} reserved of {{ordered}}",
    shipGoods: "Ship goods",
    shipTitle: "Shipment",
    deliveryNoteHint: "Your delivery note number (optional).",
    shipQty: "Quantity of {{item}} from {{location}}",
    onHandThere: "{{qty}} there",
    nothingToPick: "nothing reservable to pick in the warehouse",
    fefoHint: "Proposed earliest expiry first, then by location. Lower or clear quantities to ship less.",
    postShipment: "Post shipment",
    shipments: "Shipments",
    olderShipments: "Only the 50 newest shipments are shown.",
    confirmReverse: "Take this shipment back into stock?",
    confirmCancel: "Cancel this order? Its reservations are released; it stays in the list with its number.",
    confirmClose: "Close this order? Open quantities are closed short and their reservations released.",
    err: {
      discount: "Discount: 0 to 100 % with at most 2 decimals.",
      stale: "Someone else changed this order meanwhile. Close the editor and open it again.",
      pickCustomer: "Choose a customer.",
      customerInactive: "The customer is inactive.",
      customerNotFound: "Customer not found.",
      customerLocked: "The customer cannot change after release.",
      dateLocked: "The order date cannot change after release.",
      currencyLocked: "The currency cannot change after release.",
      shipTo: "The ship-to address does not belong to this customer.",
      shippedCannotCancel: "Goods were shipped on this order; close it instead.",
      cannotCancel: "Only draft or released orders without shipments can be cancelled.",
      cannotRelease: "Only draft orders can be released.",
      permission: "You do not have permission to manage sales orders.",
      lineRemoved: "Lines of a released order cannot be removed; close them instead.",
      dateMissing: "requested date is missing",
      service: "services cannot be sold yet",
      belowShipped: "quantity cannot be below what was already shipped",
      releasedLocked: "item, unit and warehouse cannot change once the order is released",
      warehouseRequired: "choose a ship-from warehouse",
      reservedForOthers: "This stock is reserved for other orders; reserve this order first or ship less.",
      notShippable: "Goods can be shipped only on released orders.",
      shipmentEmpty: "Enter a quantity on at least one line.",
      reverseClosed: "The order is closed; its shipments can no longer be reversed.",
      notEnoughStock: "Not enough stock at the chosen location.",
      overShip: "more than ordered would be shipped",
      lotBlocked: "the lot is blocked",
      locationNoIssue: "the location does not issue stock",
      lastLine: "This is the last open line and nothing was shipped; cancel the order instead.",
    },
  },
  priceLists: {
    purchaseTitle: "Purchase price lists",
    purchaseEmptyBody: "A purchase price list holds a supplier's prices per item, unit and quantity for a period; new purchase order lines take their price from it first.",
    supplier: "Supplier",
    selectPromptHint: "Its lines, customers and validity appear here.",
    colStatus: "Status",
    exportLines: "Export lines",
    title: "Price lists",
    listLabel: "Price lists",
    detailLabel: "Price list",
    new: "New price list",
    newTitle: "New price list",
    emptyTitle: "No price lists",
    emptyBody: "A price list sets sales prices per item, unit and quantity for chosen customers, or for every customer as the company default.",
    selectPrompt: "Select a price list",
    search: "Search code or name",
    code: "Code",
    name: "Name",
    currency: "Currency",
    validity: "Valid",
    validFrom: "Valid from",
    validTo: "Valid to",
    openEnded: "open-ended",
    lines: "Lines",
    customersCount: "Customers",
    companyDefault: "Company default",
    companyDefaultHint: "Company default for this currency (customers without their own list)",
    status: { draft: "Draft", active: "Active", closed: "Closed" },
    filter: { open: "Open", draft: "Draft", active: "Active", closed: "Closed", all: "All" },
    activate: "Activate",
    close: "Close",
    adjustPct: "Change prices by %",
    decimals: "Decimals",
    adjust: "Apply",
    customers: "Customers",
    noCustomers: "No customers yet. Add the customers this list prices.",
    noCustomersDefault: "Applies to every customer without their own list.",
    addCustomer: "Add customer",
    item: "Item",
    unit: "Unit",
    baseUnit: "base unit",
    minQty: "Quantity from",
    price: "Price",
    editPrice: "Change price",
    addLine: "Add line",
    noLines: "No lines yet.",
    searchItem: "Find item",
    more: "Show more",
    stale: "Someone changed this price list in the meantime. It has been reloaded; try again.",
    err: { supplier: "Pick a supplier.", pct: "Enter a percentage above −100 and up to 1000.", line: "Choose an item and enter a price (at most 4 decimals)." },
  },
  prt: {
    title: "Supplier returns",
    listLabel: "Supplier return list",
    detailLabel: "Supplier return details",
    new: "New return",
    newTitle: "New return to supplier",
    draft: "Draft",
    notFound: "This return does not exist or is not visible to you.",
    search: "Number, supplier or supplier RMA",
    scope: { open: "Open", all: "All" },
    status: {
      draft: "Draft",
      released: "Released",
      partially_shipped: "Partially shipped",
      shipped: "Shipped",
      closed: "Closed",
      cancelled: "Cancelled",
    },
    reason: { defective: "Defective", damaged: "Damaged", wrong_item: "Wrong item", surplus: "Surplus", other: "Other" },
    colNumber: "Return",
    colLines: "Lines",
    colCreated: "Created",
    colStatus: "Status",
    order: "Purchase order",
    orderLine: "order line {{line}}",
    orderNumber: "Purchase order number",
    orderNumberHint: "Goods go back against a received order of the active company.",
    continue: "Continue",
    supplier: "Supplier",
    supplierRma: "Supplier RMA",
    supplierRmaHint: "The supplier's return authorization number, if they gave one.",
    released: "Released",
    importRef: "Import reference",
    linesToReturn: "What goes back",
    canReturn: "can return {{qty}} of {{received}} received",
    qtyOf: "Quantity of {{item}} to return",
    reasonOf: "Reason for {{item}}",
    nothingReceived: "Nothing was received on this order.",
    saveDraft: "Save draft",
    ship: "Ship to supplier",
    shipTitle: "Ship goods back to the supplier",
    shipQtyOf: "Quantity of {{item}} shipped",
    qtyShipped: "Quantity",
    toGo: "{{qty}} to go",
    fromLocation: "From location",
    fromLocationNcr: "From location (report: {{code}})",
    lot: "Lot (received on the order)",
    shipHint: "Quantities in the item's base unit. Any location of the warehouse, quarantine included; empty = the report's location or DEFAULT.",
    nothingToGo: "Everything released has been shipped.",
    postShipment: "Post shipment",
    shipments: "Shipments",
    confirmReverse: "Reverse this shipment?",
    action: { release: "Release", cancel: "Cancel return", close: "Close return" },
    confirm: {
      release: "Release this return?",
      cancel: "Cancel this return? It cannot be undone.",
      close: "Close this return? What has not gone back stays in stock.",
    },
    returnToSupplier: "Return to supplier",
    returns: "Returns to supplier",
    emptyTitle: "No supplier returns",
    emptyBody: "A return sends goods received on a purchase order back to the supplier — by hand or from a nonconformance report.",
    selectPrompt: "Select a return",
    selectPromptHint: "Its lines and shipments appear here.",
    err: {
      orderNotFound: "No purchase order with this number in the active company.",
      noLines: "Enter a quantity on at least one line.",
      nothingToShip: "Enter a quantity on at least one line.",
      stale: "The return was changed by someone else; reload it.",
      notReceived: "Goods can be returned only on received orders.",
      closeInstead: "Goods were shipped on this return; close it instead.",
      reversed: "This shipment is already reversed.",
      ncrOpen: "A line from a nonconformance report is not shipped yet; ship it or cancel the return.",
      over: "More than can still be returned on this order line.",
      lotRequired: "Choose the lot (one received on the order).",
      lotNotReceived: "That lot was not received on this order line.",
      serialNotReceived: "A serial number was not received on this order line.",
      twice: "The order line appears twice.",
      overReleased: "More than released would be shipped.",
    },
  },
  rma: {
    title: "Customer returns",
    listLabel: "Return list",
    detailLabel: "Return details",
    new: "New return",
    newTitle: "New return",
    draft: "Draft",
    notFound: "This return does not exist or is not visible to you.",
    search: "Number, customer or reference",
    scope: { open: "Open", all: "All" },
    status: {
      draft: "Draft",
      authorized: "Authorized",
      partially_received: "Partially received",
      received: "Received",
      closed: "Closed",
      cancelled: "Cancelled",
    },
    reason: { damaged: "Damaged", defective: "Defective", wrong_item: "Wrong item", not_needed: "Not needed", other: "Other" },
    colNumber: "RMA",
    colLines: "Lines",
    colCreated: "Created",
    colStatus: "Status",
    order: "Sales order",
    orderLine: "order line {{line}}",
    orderNumber: "Sales order number",
    orderNumberHint: "Goods come back against a shipped order of the active company.",
    continue: "Continue",
    customer: "Customer",
    customerReference: "Customer reference",
    authorized: "Authorized",
    importRef: "Import reference",
    linesToReturn: "What comes back",
    canReturn: "can return {{qty}} of {{shipped}} shipped",
    qtyOf: "Quantity of {{item}} to return",
    reasonOf: "Reason for {{item}}",
    inspect: "Inspect",
    inspectHint: "Inspected lines are received into the warehouse's quarantine location with an inspection order; quantities are in the item's base unit.",
    nothingShipped: "Nothing was shipped on this order.",
    saveDraft: "Save draft",
    receive: "Receive goods",
    receiveTitle: "Receive returned goods",
    receiveQtyOf: "Quantity of {{item}} received",
    qtyReceived: "Quantity",
    expected: "{{qty}} expected",
    location: "Location",
    putaway: "Putaway location",
    lot: "Lot (shipped on the order)",
    quarantineHint: "Goes to quarantine first; the location above is where it goes when the inspection accepts it.",
    nothingExpected: "Everything authorized has been received.",
    postReceipt: "Post receipt",
    receipts: "Receipts",
    inspections: "Inspections",
    confirmReverse: "Reverse this receipt?",
    action: { authorize: "Authorize", cancel: "Cancel return", close: "Close return" },
    confirm: {
      authorize: "Authorize this return?",
      cancel: "Cancel this return? It cannot be undone.",
      close: "Close this return? What has not come back is no longer expected.",
    },
    returnGoods: "Return goods",
    returns: "Returns",
    emptyTitle: "No returns",
    emptyBody: "A return (RMA) takes back goods shipped on a sales order: authorize it, receive the goods, inspect them when needed.",
    selectPrompt: "Select a return",
    selectPromptHint: "Its lines, receipts and inspections appear here.",
    err: {
      orderNotFound: "No sales order with this number in the active company.",
      noLines: "Enter a quantity on at least one line.",
      nothingToReceive: "Enter a quantity on at least one line.",
      stale: "The return was changed by someone else; reload it.",
      notShipped: "Goods can be returned only on shipped orders.",
      closeInstead: "Goods were received on this return; close it instead.",
      decided: "The inspection of this receipt is decided; its stock has moved — handle it there.",
      reversed: "This receipt is already reversed.",
      over: "More than can still be returned on this order line.",
      lotRequired: "Choose the lot (one shipped on the order).",
      lotNotShipped: "That lot was not shipped on this order line.",
      serialNotShipped: "A serial number was not shipped on this order line.",
      twice: "The order line appears twice.",
      overAuthorized: "More than authorized would be received.",
    },
  },
  discounts: {
    title: "Discounts",
    new: "New discount",
    newTitle: "New discount",
    detailLabel: "Discount",
    customer: "Customer",
    item: "Item",
    itemHint: "Leave the item empty for the customer's whole range; a rule for the item wins over it.",
    allItems: "All items",
    pct: "Discount %",
    minQty: "From quantity",
    minQtyHint: "In the item's base unit; the highest minimum at or below the line quantity wins.",
    validFrom: "Valid from",
    validTo: "Valid to",
    validity: "Valid",
    delete: "Delete",
    search: "Customer or item code",
    scope: { current: "Current and future", all: "All" },
    emptyTitle: "No discounts",
    emptyBody: "A discount takes a % off the price list or list price for a customer — on one item or the whole range, from a quantity, for a period.",
    selectPrompt: "Select a discount",
    selectPromptHint: "Its %, minimum and validity appear here.",
    err: {
      customer: "Pick a customer.",
      pct: "Discount: above 0 and at most 100 %, 2 decimals.",
      min: "From quantity: zero or more.",
      overlap: "Another discount of this customer, item and minimum covers part of this period.",
    },
  },
  serialList: {
    title: "Serial numbers",
    listLabel: "Serial numbers",
    detailLabel: "Serial number",
    colSerial: "Serial no.",
    colItem: "Item",
    colDescription: "Description",
    colStatus: "Status",
    colWhere: "Where",
    colWarehouse: "Warehouse",
    colLocation: "Location",
    colLot: "Lot",
    colProject: "Project",
    where: "Where it is",
    registered: "Registered",
    trace: "History",
    traceCap: "Showing the latest 500 movements.",
    filterInStock: "In stock",
    searchPlaceholder: "Serial number (prefix)",
    emptyTitle: "No serial numbers",
    emptyBody: "Units of serial-controlled items appear here once they are received, reported or adjusted in.",
    selectPrompt: "Select a serial number",
    selectPromptHint: "See where the unit is and every movement of it.",
    receiptLabels: "Serial labels",
    postingLabels: "Serial labels",
    status: { in_stock: "In stock", issued: "Issued" },
  },
  serials: {
    label: "Serial numbers of {{item}}",
    placeholder: "Scan or type serial numbers, one per line — or a range SN-001..SN-050",
    count_one: "{{count}} serial number",
    count_few: "{{count}} serial numbers",
    count_many: "{{count}} serial numbers",
    count_other: "{{count}} serial numbers",
    countOf_one: "{{count}} of {{expected}} serial numbers",
    countOf_few: "{{count}} of {{expected}} serial numbers",
    countOf_many: "{{count}} of {{expected}} serial numbers",
    countOf_other: "{{count}} of {{expected}} serial numbers",
    invalid: "not valid: {{list}}",
    duplicates: "given twice: {{list}}",
    more: "+{{count}} more",
    truncated: "at most 10,000 per line",
    err: {
      required: "enter the serial numbers of the units",
      count: "the number of serial numbers must equal the quantity",
    },
  },
  po: {
    title: "Purchase orders",
    newOrder: "New order",
    listLabel: "Purchase order list",
    filterOpen: "Open",
    filterAll: "All",
    searchPlaceholder: "Order or supplier…",
    colOrder: "Order",
    colSupplier: "Supplier",
    colStatus: "Status",
    colDelivery: "Delivery",
    colAmount: "Amount",
    late: "late",
    emptyTitle: "No purchase orders match",
    emptyBody: "Try another status or clear the search.",
    clearFilters: "Clear filters",
    detailLabel: "Order details",
    close: "Close details",
    orderDate: "Order date",
    promised: "Promised delivery",
    warehouse: "Warehouse",
    lines: "Lines",
    selectPrompt: "Select an order to see its details",
    selectPromptHint: "Use ↑ ↓ and Enter, or click a row.",
    pickSupplier: "Search supplier…",
    noSuppliers: "No active supplier matches.",
    supplierReference: "Supplier reference",
    note: "Note",
    currency: "Currency",
    createdBy: "Created by",
    revision: "Revision",
    unitPrice: "Unit price",
    noPrice: "No price",
    lineClosed: "Closed short",
    closeLine: "Close line",
    receivedSoFar: "{{qty}} received; item cannot change",
    leadTimeHint_one: "Empty delivery dates default to the order date + {{count}} day (the item's lead time with this supplier, else the supplier's).",
    leadTimeHint_few: "Empty delivery dates default to the order date + {{count}} days (the item's lead time with this supplier, else the supplier's).",
    leadTimeHint_many: "Empty delivery dates default to the order date + {{count}} days (the item's lead time with this supplier, else the supplier's).",
    leadTimeHint_other: "Empty delivery dates default to the order date + {{count}} days (the item's lead time with this supplier, else the supplier's).",
    release: "Release to supplier",
    saveAndRelease: "Save and release",
    cancelOrder: "Cancel order",
    closeOrder: "Close order",
    confirmCancel: "Cancel this order? It stays in the list with its number but can no longer be changed.",
    confirmClose: "Close this order? Open quantities are closed short; nothing more can be received.",
    receiveGoods: "Receive goods",
    receiveTitle: "Goods receipt",
    deliveryNote: "Delivery note",
    deliveryNoteHint: "The supplier's delivery note number (optional).",
    open: "Open",
    receiveQty: "Received quantity of {{item}}",
    supplierLot: "Supplier lot",
    nothingOpen: "No open lines to receive.",
    postReceipt: "Post receipt",
    olderReceipts: "Only the 50 newest receipts are shown.",
    receipts: "Receipts",
    reversal: "Reversal",
    reversed: "Reversed",
    reverse: "Reverse",
    confirmReverse: "Take this delivery back out of stock?",
    err: {
      receiptPermission: "You do not have permission to receive goods.",
      notReceivable: "Goods can be received only on released orders.",
      receiptEmpty: "Enter a quantity on at least one line.",
      reverseClosed: "The order is closed; its receipts can no longer be reversed.",
      alreadyReversed: "This receipt is already reversed.",
      reverseMoved: "The received stock has moved meanwhile; move it back before reversing.",
      subcontractShort: "Not enough material at the subcontractor for this receipt — send it first.",
      subcontractSerial: "Serial-controlled material is not consumed by a receipt at the subcontractor.",
      sendNotOpen: "Material is sent only for open lines of released orders.",
      overTolerance: "more than ordered plus the tolerance",
      orderLineClosed: "the order line is closed",
      lotCode: "lot code: letters, digits and . _ / - (at most 40)",
      line: "Line {{line}}: {{message}}",
      stale: "Someone else changed this order meanwhile. Close the editor and open it again.",
      pickSupplier: "Choose a supplier.",
      price: "Enter the price with at most 4 decimals.",
      supplierInactive: "The supplier is inactive.",
      supplierNotFound: "Supplier not found.",
      supplierLocked: "The supplier cannot change after release.",
      currencyLocked: "The currency cannot change after release.",
      currency: "Use a three-letter currency code, e.g. PLN.",
      warehouse: "The warehouse is inactive or belongs to another company.",
      permission: "You do not have permission to manage purchase orders.",
      noLines: "Add at least one line.",
      receivedCannotCancel: "Goods were received on this order; close it instead.",
      cannotCancel: "Only draft or released orders without receipts can be cancelled.",
      cannotRelease: "Only draft orders can be released.",
      priceMissing: "price is missing",
      dateMissing: "delivery date is missing",
      itemInactive: "the item is inactive",
      incompleteLine: "Every line needs an item and a quantity above zero.",
      qtyPositive: "quantity must be above zero",
      service: "services cannot be ordered yet",
      belowReceived: "quantity cannot be below what was already received",
      receivedLocked: "item, unit and warehouse cannot change once goods were received",
      lineClosed: "the line is closed and cannot change",
    },
  },
  list: {
    keyboardHint: "↑↓ move · Esc close details",
    loadedCount: "{{count}} loaded",
    hasMore: "scroll for more",
    endOfList: "end of list",
    errorTitle: "Couldn't load the list",
    retry: "Try again",
    clearFilters: "Clear filters",
  },
  form: {
    save: "Save",
    cancel: "Cancel",
    edit: "Edit",
    saving: "Saving…",
    saved: "Saved",
    required: "Required",
    add: "Add",
    remove: "Remove",
    readOnly: "You can view this. Ask an administrator for edit rights.",
  },
  itemType: {
    material: "Material",
    semi_finished: "Semi-finished",
    finished_good: "Finished good",
    service: "Service",
  },
  items: {
    sales: {
      title: "Sales data",
      edit: "Change sales data",
      unit: "Sales unit",
      baseUnit: "base unit",
      unitHint: "New order lines take this unit.",
      listPrice: "List price",
      currency: "Currency",
      minQty: "Minimum quantity",
      multiple: "Multiple",
      sellable: "Sellable",
      yes: "Yes",
      no: "No",
      hint: "New sales order lines: the customer's price list first, then the company default list, the customer's last price, else this list price (same unit and currency). Minimum and multiple apply in the sales unit.",
      customerCodes: "Customers' item codes",
      noCodes: "No customer codes yet.",
      customer: "Customer",
      customerCode: "Customer's code",
      customerDescription: "Customer's description",
      addCode: "Add code",
      lastPrices: "Last prices per customer",
      noPrices: "No released orders yet.",
      price: "Price",
      order: "Order",
      err: {
        price: "Price: zero or more with at most 4 decimals.",
        qty: "Quantities: zero or more.",
        pair: "A list price needs a currency (and a currency a price).",
        code: "Pick a customer and enter the code.",
        codeTaken: "The customer already uses this code, or already has a code for this item.",
      },
    },
    buying: {
      title: "Purchasing data",
      edit: "Change purchasing data",
      unit: "Purchase unit",
      unitHint: "New purchase order lines take this unit.",
      standardPrice: "Standard price",
      purchasable: "Purchased",
      hint: "New purchase order lines: the supplier's agreed price first, then the last price paid to that supplier, else this standard price (same unit and currency).",
      mrpSupplier: "MRP supplier (planning parameters)",
      suppliers: "Approved suppliers",
      noSuppliers: "No suppliers listed. Add one with its code, agreed price and lead time.",
      supplier: "Supplier",
      addSupplier: "Add supplier",
      editSupplier: "Change supplier item",
      supplierForm: "Supplier item",
      supplierCode: "Supplier's code",
      supplierDescription: "Supplier's description",
      priceUnit: "Unit",
      agreedPrice: "Agreed price",
      noAgreedPrice: "No agreed price",
      leadTime: "Lead time (days)",
      min: "Minimum",
      multiple: "Multiple",
      lastPrices: "Last prices paid",
      noPrices: "No released purchase orders yet.",
      err: {
        supplier: "Pick a supplier.",
        lead: "Lead time: 0 to 365 days.",
        taken: "The supplier is already listed for this item, or already uses this code.",
      },
    },
    tabs: {
      label: "Item data by department",
      general: "General",
      purchasing: "Purchasing",
      sales: "Sales",
      warehousing: "Warehousing",
      production: "Production",
      planning: "Planning",
      quality: "Quality",
      related: "Related",
      stock: "Stock of this item",
      purchasingHint: "Supplier, buyer and receipt warehouse for MRP are planning parameters (Planning tab); the purchasing data and approved suppliers below feed new purchase order lines.",
    },
    frozen: "This item already has stock movements, so its base unit, type and tracking can no longer change. Create a new item instead.",
    title: "Items",
    newItem: "New item",
    listLabel: "Item list",
    searchPlaceholder: "Code or description…",
    filterAll: "All",
    showInactive: "Show inactive",
    colCode: "Code",
    colDescription: "Description",
    colType: "Type",
    colUom: "Unit",
    colTracking: "Tracking",
    inactive: "Inactive",
    trackingNone: "Not tracked",
    trackingLot: "Lot",
    trackingSerial: "Serial",
    emptyTitle: "No items match",
    emptyBody: "Try another type or clear the search.",
    detailLabel: "Item details",
    selectPrompt: "Select an item to see its details",
    selectPromptHint: "Use ↑ ↓, or click a row.",
    createTitle: "New item",
    close: "Close details",
    code: "Code",
    codeHint: "Upper-case letters, digits and . _ / - (max 40). Cannot be changed later.",
    codeInvalid: "Use upper-case letters, digits and . _ / - only.",
    codeTaken: "An item with this code already exists.",
    description: "Description",
    type: "Type",
    baseUom: "Base unit",
    baseUomHint: "Stock is kept in this unit.",
    procurement: "Procurement",
    purchase: "Purchased",
    manufacture: "Manufactured",
    tracking: "Tracking",
    lotControlled: "Lot controlled",
    serialControlled: "Serial numbers",
    serviceNoTracking: "Services cannot be lot or serial tracked.",
    active: "Active",
    status: "Status",
    lastChanged: "Last changed",
    conversions: "Unit conversions",
    conversionsEmpty: "Only the base unit. Add a conversion to order or count in another unit.",
    conversionRule: "1 {{from}} = {{factor}} {{to}}",
    conversionUnit: "Unit",
    conversionFactor: "equals (in {{base}})",
    conversionInvalid: "Enter a positive number.",
    conversionExists: "This unit already has a conversion.",
  },
  units: {
    title: "Units of measure",
    listLabel: "Units of measure",
    colCode: "Code",
    colName: "Name",
    colDimension: "Dimension",
    colDecimals: "Decimals",
    colStatus: "Status",
    globalConversions: "Standard conversions",
    globalConversionsHint: "Apply to every item unless the item has its own conversion.",
    note: "Units are shared by all companies in the organization.",
  },
  dimension: {
    count: "Count",
    mass: "Mass",
    length: "Length",
    area: "Area",
    volume: "Volume",
    time: "Time",
  },
  warehouseType: {
    main: "Main",
    production: "Production",
    quarantine: "Quarantine",
    transit: "Transit",
    subcontractor: "Subcontractor",
  },
  warehouses: {
    subcontractor: "Subcontractor",
    subcontractorRequired: "Choose the subcontractor (supplier) of this warehouse.",
    nettable: "Counted in MRP",
    nettableYes: "Yes",
    nettableNo: "No",
    nettableHint: "Stock, demand and supply of this warehouse count in planning. Usually off for quarantine and transit.",
    quarantineLocation: "Quarantine location",
    quarantineHint: "Inspected receipts wait here until the quality decision. Empty: stock stays where it was received.",
    title: "Warehouses",
    newWarehouse: "New warehouse",
    listLabel: "Warehouse list",
    detailLabel: "Warehouse details",
    colCode: "Code",
    colName: "Name",
    colSite: "Site",
    colCompany: "Company",
    colType: "Type",
    colNegative: "Negative stock",
    negativeAllowed: "Allowed",
    showInactive: "Show inactive",
    emptyTitle: "No warehouses yet",
    emptyBody: "Create a site and its first warehouse. Each warehouse gets a DEFAULT location automatically.",
    selectPrompt: "Select a warehouse to see its details",
    selectPromptHint: "Use ↑ ↓ or click a row.",
    createTitle: "New warehouse",
    code: "Warehouse code",
    codeHint: "Unique in the company; cannot be changed later.",
    codeInvalid: "Use A–Z, 0–9, - and _ (max 16).",
    codeTaken: "This code is already used in the company.",
    name: "Name",
    company: "Company",
    site: "Site",
    newSite: "New site",
    siteCode: "Site code",
    siteName: "Site name",
    siteCreate: "Create site",
    siteTaken: "This site code is already used in the company.",
    noSites: "No sites in this company yet — create one first.",
    type: "Type",
    allowNegative: "Allow negative stock",
    allowNegativeHint: "Postings may take on-hand below zero (e.g. backflush in production). Off by default.",
    active: "Active",
    inactive: "Inactive",
    status: "Status",
    lastChanged: "Last changed",
    openLocations: "Open locations",
    siteHasWarehouses: "Deactivate the site's warehouses first.",
  },
  locations: {
    title: "Locations",
    warehouse: "Warehouse",
    listLabel: "Location list",
    detailLabel: "Location details",
    newLocation: "New location",
    generate: "Generate",
    colCode: "Location",
    colDescription: "Description",
    colZone: "Zone",
    colFlags: "Use",
    receive: "Receive",
    pick: "Pick",
    blocked: "Blocked",
    default: "Default",
    showInactive: "Show inactive",
    searchPlaceholder: "Location code starts with…",
    emptyTitle: "No locations match",
    emptyBody: "Clear the search, or generate locations for a rack.",
    noWarehouses: "Create a warehouse first.",
    selectPrompt: "Select a location to see its details",
    selectPromptHint: "Use ↑ ↓ or click a row.",
    createTitle: "New location",
    code: "Location code",
    codeHint: "Unique in the warehouse; cannot be changed later.",
    codeInvalid: "Use A–Z, 0–9, . / - (max 30).",
    codeTaken: "This location already exists in the warehouse.",
    description: "Description",
    zone: "Zone",
    zoneHint: "Optional grouping, e.g. A or COLD.",
    zoneInvalid: "Use A–Z, 0–9, - and _ (max 16).",
    isReceivable: "Can receive stock",
    isPickable: "Can issue stock",
    isBlocked: "Blocked",
    isBlockedHint: "No movements in or out until unblocked.",
    active: "Active",
    defaultNote: "Created with the warehouse. Postings without a bin land here, so it cannot be blocked or deactivated.",
    defaultLocked: "The DEFAULT location cannot be blocked or deactivated.",
    lastChanged: "Last changed",
    generateTitle: "Generate locations",
    pattern: "Pattern",
    patternHint: "Ranges in braces: A-{01..06}-{01..04} gives A-01-01 … A-06-04.",
    preview: "{{count}} locations: {{first}} … {{last}}",
    previewOne: "1 location: {{first}}",
    patternEmpty: "Enter a pattern.",
    patternBadRange: "Range {{range}} is not valid. Use {01..10} or {A..D}.",
    patternTooMany: "{{count}} locations — at most {{max}} per run.",
    patternBadCode: "{{code}} is not a valid location code.",
    generateSubmit: "Create {{count}} locations",
    generated: "Created {{created}} of {{count}}; the rest already existed.",
    generatedAll: "Created {{created}} locations.",
  },
  txType: {
    adjustment_in: "Adjustment in",
    adjustment_out: "Adjustment out",
    transfer_out: "Transfer out",
    transfer_in: "Transfer in",
    purchase_receipt: "Purchase receipt",
    purchase_return: "Purchase return",
    sales_issue: "Sales issue",
    sales_return: "Sales return",
    production_issue: "Issue to production",
    production_receipt: "Production receipt",
    count_adjustment: "Count adjustment",
  },
  stock: {
    ofWhichProject: "{{qty}} project stock",
    colProject: "Project stock",
    projectStock: "Project {{code}}",
    title: "Inventory",
    listLabel: "Stock by item",
    detailLabel: "Item stock",
    colItem: "Item",
    colDescription: "Description",
    colWhere: "Where",
    colOnHand: "On hand",
    colReserved: "Reserved",
    colAvailable: "Available",
    locationsCount_one: "{{count}} location",
    locationsCount_few: "{{count}} locations",
    locationsCount_many: "{{count}} locations",
    locationsCount_other: "{{count}} locations",
    allWarehouses: "All warehouses",
    emptyTitle: "No stock yet",
    emptyBody: "Stock appears here once goods are received or adjusted in.",
    emptyFilteredTitle: "Nothing in stock matches",
    emptyFilteredBody: "Try another warehouse or clear the search.",
    selectPrompt: "Select an item to see where its stock is",
    selectPromptHint: "Use ↑ ↓ or click a row.",
    whereTitle: "Where it is",
    movementsTitle: "Latest movements",
    noStock: "No stock on hand.",
    noMovements: "No movements yet.",
    lot: "Lot",
    expires: "expires {{date}}",
    reversal: "Reversal",
    openItem: "Open item",
    moreBalances: "Showing the first 200 places. Filter the overview by warehouse to see the rest.",
  },
  wo: {
    title: "Warehouse orders",
    listLabel: "Warehouse order list",
    detailLabel: "Warehouse order details",
    scope: { open: "To do", done: "Done", all: "All" },
    direction: { all: "All directions", inbound: "Inbound", outbound: "Outbound", transfer: "Transfers", quality: "Quality" },
    kind: {
      purchase_receipt: "Purchase receipt",
      sales_shipment: "Sales shipment",
      production_issue: "Material issue",
      production_receipt: "Production receipt",
      transfer: "Transfer",
      inspection: "Inspection",
    },
    colNumber: "Order",
    colKind: "Origin",
    colDate: "Planned",
    colWarehouse: "Warehouse",
    colParty: "Partner / item",
    colLines: "Lines",
    colStatus: "Status",
    allWarehouses: "All warehouses",
    searchPlaceholder: "Order number…",
    draftTransfer: "Draft transfer",
    emptyTitle: "Nothing for the warehouse here",
    emptyBody: "Released purchase and sales orders, released production orders, draft transfers and open inspections appear here.",
    selectPrompt: "Select a warehouse order",
    selectHint: "Its lines show what to move, what is done and where.",
    lines: "Lines",
    colItem: "Item",
    colQty: "Quantity",
    colDone: "Done",
    colFrom: "From",
    colTo: "To",
    openSource: "Open the {{kind}}",
    hint: "Derived from the origin documents — post receipts, shipments, issues and transfers in their own screens.",
  },
  documents: {
    reasonDescription: "Reason description",
    adjustmentsTitle: "Stock adjustments",
    transfersTitle: "Transfers",
    newAdjustment: "New adjustment",
    quickTransfer: "Quick transfer",
    quickTransferHint: "Moves stock between locations of one site and posts immediately.",
    detailLabel: "Document details",
    colNumber: "Number",
    colDate: "Date",
    colStatus: "Status",
    filterAll: "All",
    status: { draft: "Draft", posted: "Posted", cancelled: "Cancelled" },
    draftTitle: "Draft",
    emptyTitle: "No documents yet",
    emptyBody: "Create the first one; posted documents move stock through the ledger.",
    selectPrompt: "Select a document to see its lines",
    selectPromptHint: "Use ↑ ↓ or click a row.",
    reason: "Reason",
    note: "Note",
    createdAt: "Created",
    postedAt: "Posted",
    linesShown: "Showing the first {{shown}} of {{total}} lines. The full list is in the stock movements.",
    lines_one: "{{count}} line",
    lines_few: "{{count}} lines",
    lines_many: "{{count}} lines",
    lines_other: "{{count}} lines",
    linesTitle: "Lines",
    noLines: "No lines yet.",
    item: "Item",
    location: "Location",
    expiry: "Expiry date",
    qty: "Quantity",
    direction: "Direction",
    in: "In",
    out: "Out",
    from: "From",
    to: "To",
    addLine: "Add line",
    pickItem: "Item code or name…",
    noItems: "No items match",
    noLocations: "No locations match",
    noLotStock: "No lot in stock here",
    saveDraft: "Save draft",
    saveAndPost: "Save and post",
    post: "Post",
    postTransfer: "Post transfer",
    cancelDoc: "Cancel document",
    reverse: "Reverse",
    reversal: "Reversal",
    reversalOf: "Reversal of",
    reversedBy: "Reversed by",
    seeStock: "See current stock",
    err: {
      line: "Line {{line}}: {{message}}",
      notEnough: "Not enough stock of {{item}} in {{location}}.",
      lotRequired: "this item needs a lot.",
      notLotControlled: "this item has no lots.",
      lotNotFound: "this lot does not exist for the item.",
      lotBlocked: "the lot is blocked.",
      lotAttributes: "the lot exists with a different expiry or supplier lot.",
      locationBlocked: "the location is blocked.",
      locationInactive: "the location is inactive.",
      locationNoReceive: "the location does not receive stock.",
      locationNoIssue: "the location does not issue stock.",
      itemInactive: "the item is inactive.",
      serial: "a serial-numbered quantity must be whole units.",
      warehouseInactive: "the warehouse is inactive.",
      decimals: "the quantity has more decimals than the unit allows.",
      noConversion: "there is no conversion to the item's base unit.",
      reasonIn: "This reason allows only receipts (positive quantities).",
      reasonOut: "This reason allows only issues (negative quantities).",
      noLines: "Add at least one line.",
      transferSite: "Both locations must belong to the same company and site.",
      incompleteLine: "Every line needs an item, a location and a quantity above zero.",
      locationCounting: "location {{location}} is being counted ({{number}}).",
      pickLot: "Pick the lot to move.",
    },
  },
  counts: {
    scanSerials: "Scan serials",
    serialsOf: "Serial numbers at {{location}}",
    serialHint: "Scan every unit you find here, one per line (or a range). Units in stock here that you do not scan are booked out; scanned units that are not in stock here are booked in.",
    saveSerials: "Save count",
    serialCount_one: "{{count}} unit",
    serialCount_few: "{{count}} units",
    serialCount_many: "{{count}} units",
    serialCount_other: "{{count}} units",
    colWarehouse: "Warehouse",
    colLines: "Lines",
    colCounted: "Counted",
    importCounted: "Import counted quantities",
    title: "Cycle counts",
    newCount: "New count",
    draftTitle: "Draft",
    status: { draft: "Draft", in_progress: "Counting", posted: "Posted", cancelled: "Cancelled" },
    emptyTitle: "No cycle counts yet",
    emptyBody: "Pick a zone or a range of locations; starting the count locks them until it is posted.",
    selectPrompt: "Select a count to see its lines",
    warehouse: "Warehouse",
    selection: "Selection",
    progress: "Progress",
    locationsLabel: "Locations",
    startedAt: "Started",
    mode: { all: "Whole warehouse", zone: "Zone", range: "Code range" },
    modeHint: {
      all: "Every active location of the warehouse (up to 2000 locations and 1000 stock lines).",
      zone: "Locations with this zone.",
      range: "Location codes from … to …; the end code also covers its sub-locations (A-02 → A-02-05).",
    },
    zone: "Zone",
    codeFrom: "From code",
    codeTo: "To code",
    scopeAll: "Whole warehouse",
    scopeZone: "Zone {{zone}}",
    scopeRange: "Locations {{from}} – {{to}}",
    scopeLocations_one: "{{count}} location",
    scopeLocations_few: "{{count}} locations",
    scopeLocations_many: "{{count}} locations",
    scopeLocations_other: "{{count}} locations",
    saveAndStart: "Save and start counting",
    start: "Start counting",
    post: "Approve and post differences",
    cancelCount: "Cancel count",
    openLines_one: "{{count}} line still to count.",
    openLines_few: "{{count}} lines still to count.",
    openLines_many: "{{count}} lines still to count.",
    openLines_other: "{{count}} lines still to count.",
    lockedHint: "These locations are locked: no other movement is possible until the count is posted or cancelled.",
    blindHint: "Count what is physically there. System quantities are hidden until the count is approved.",
    entryTitle: "Record a count",
    countedQty: "Counted",
    record: "Record",
    recorded: "{{item}} in {{location}}: {{qty}}",
    added: "new line",
    addedBadge: "found",
    lineFilter: "Show lines",
    filter: { all: "All", uncounted: "To count", differences: "Differences" },
    noLines: {
      all: "No stock in the selected locations. Record what you find.",
      uncounted: "Everything is counted.",
      differences: "No differences.",
    },
    systemQty: "System quantity",
    difference: "Difference",
    countedFor: "Counted {{item}} in {{location}}",
    err: {
      entry: "Entry {{entry}}: {{message}}",
      negative: "a counted quantity cannot be negative.",
      serialUnit: "serial items are counted in the base unit.",
      serialOutside: "serial {{serial}} is in stock at {{location}}, which this count does not cover — count it there or transfer it first.",
      serialElsewhere: "serial {{serial}} is already counted on line {{line}}.",
      serialTwice: "serial {{serial}} is scanned twice.",
      notInCount: "the location is not part of this count.",
      lotCode: "the lot code is not valid.",
      service: "services are not stocked.",
      twice: "the same line is counted twice.",
      busy: "Location {{location}} is already being counted in {{number}}.",
      uncounted_one: "{{count}} line is not counted yet.",
      uncounted_few: "{{count}} lines are not counted yet.",
      uncounted_many: "{{count}} lines are not counted yet.",
      uncounted_other: "{{count}} lines are not counted yet.",
      noLocations: "No active locations match the selection.",
      tooBig: "The selection is too large for one count; narrow it by zone or code range.",
      state: "The count changed in the meantime; refresh and try again.",
      incomplete: "Pick a location and an item and enter the counted quantity.",
      pickWarehouse: "Pick a warehouse.",
      pickZone: "Enter the zone.",
    },
  },
  partners: {
    roleFilter: "Partner role",
    roleAll: "All partners",
    colRoles: "Roles",
    all: {
      title: "Business partners",
      newPartner: "New partner",
      selectPrompt: "Select a partner to see its details",
      emptyTitle: "No business partners yet",
      emptyBody: "Add the companies you buy from and sell to; one partner can be both.",
      emptyFilteredTitle: "No partners match",
      emptyFilteredBody: "Try another search or show inactive partners.",
    },
    supplier: {
      title: "Suppliers",
      newPartner: "New supplier",
      selectPrompt: "Select a supplier to see its details",
      emptyTitle: "No suppliers yet",
      emptyBody: "Add the companies you buy from; purchase orders pick them from here.",
      emptyFilteredTitle: "No suppliers match",
      emptyFilteredBody: "Try another search or show inactive suppliers.",
    },
    customer: {
      title: "Customers",
      newPartner: "New customer",
      selectPrompt: "Select a customer to see its details",
      emptyTitle: "No customers yet",
      emptyBody: "Add the companies you sell to; sales orders pick them from here.",
      emptyFilteredTitle: "No customers match",
      emptyFilteredBody: "Try another search or show inactive customers.",
    },
    paymentTerms: "Payment terms",
    paymentTermsHint: "Printed on sales documents, e.g. 30 days net.",
    createTitle: "New business partner",
    detailLabel: "Partner details",
    selectPromptHint: "Use ↑ ↓ or click a row.",
    searchPlaceholder: "Search code, name or tax ID…",
    showInactive: "Show inactive",
    colCode: "Code",
    colName: "Name",
    code: "Code",
    codeHint: "Upper case letters, digits, . _ -; cannot change later.",
    codeInvalid: "Use upper case letters, digits and . _ - (max 20).",
    codeTaken: "This code is already used by another partner.",
    name: "Name",
    taxId: "Tax ID",
    country: "Country",
    countryHint: "Two letters, e.g. PL.",
    countryInvalid: "Use a two-letter country code, e.g. PL.",
    email: "Email",
    emailInvalid: "Enter a valid email address.",
    phone: "Phone",
    currency: "Currency",
    currencyHint: "Default for orders, e.g. PLN.",
    currencyInvalid: "Use a three-letter currency code, e.g. PLN.",
    roles: "Roles",
    roleSupplier: "Supplier",
    roleCustomer: "Customer",
    roleRequired: "Choose at least one role.",
    leadTime: "Lead time",
    leadTimeDays: "Lead time (days)",
    leadTimeInvalid: "Enter whole days, at most 365.",
    days_one: "{{count}} day",
    days_few: "{{count}} days",
    days_many: "{{count}} days",
    days_other: "{{count}} days",
    tolerance: "Over-receipt tolerance",
    toleranceHint: "% a delivery may exceed the ordered quantity.",
    toleranceInvalid: "Enter a percentage between 0 and 100.",
    note: "Note",
    active: "Active",
    inactive: "Inactive",
    addresses: "Addresses",
    addressesEmpty: "No addresses yet.",
    addAddress: "Add address",
    addressIncomplete: "Street, city and a two-letter country are required.",
    kindLabel: "Address type",
    kind: { main: "Main address", ship_from: "Ships from", ship_to: "Ships to" },
    default: "Default",
    street: "Street and number",
    postalCode: "Postal code",
    city: "City",
  },
  users: {
    title: "Users",
    invite: "Invite user",
    tab: { members: "Members", invitations: "Invitations" },
    colName: "Name",
    colStatus: "Status",
    email: "Email",
    fullName: "Full name",
    fullNameHint: "Optional; the person can change it.",
    role: "Role",
    roles: "Roles",
    noRoles: "No roles: this member can sign in but sees nothing.",
    pickRole: "Choose a role…",
    statusActive: "Active",
    statusDisabled: "Disabled",
    you: "You",
    joined: "Member since",
    lastSignIn: "Last sign-in",
    never: "Never",
    disable: "Disable access",
    enable: "Restore access",
    selfNote: "You cannot change your own roles or access. Ask another administrator.",
    detailLabel: "User details",
    selectPrompt: "Select a member to manage roles and access",
    selectInvitation: "Select an invitation to see its details",
    selectPromptHint: "Use ↑ ↓ or click a row.",
    emptyMembersTitle: "No members yet",
    emptyMembersBody: "Invite the people who should work in Patronix.",
    emptyInvitationsTitle: "No invitations",
    emptyInvitationsBody: "Invitations you send appear here until they are accepted.",
    invitation: "Invitation",
    invited: "Invited",
    expires: "Expires",
    acceptedAt: "Accepted",
    inv: { pending: "Pending", expired: "Expired", accepted: "Accepted", revoked: "Revoked" },
    inviteTitle: "Invite user",
    inviteIntro: "Only invited people can get in. Patronix creates a personal link for this email address; you send it yourself (copy it or open it in your mail app) — no email is sent automatically yet.",
    sendInvite: "Create invitation link",
    howToJoin: "Send this link to the person — Patronix does not email it. It is shown only now. They sign up, or sign in if they already have a Patronix account, within 14 days.",
    linkShownOnce: "The link is shown only when the invitation is created. Renew it to get a new link (the old one stops working), then send it yourself.",
    openInMail: "Open in mail app",
    mailSubject: "Invitation to Patronix ERP",
    mailBody: "Hello,\n\nyou are invited to Patronix ERP. Create your account with this personal link (valid for 14 days, only for this email address):\n\n{{link}}\n\nAfter signing up, confirm your email address with the message you receive.",
    joinLink: "Sign-up link",
    copy: "Copy",
    copied: "Copied",
    extend: "Renew for 14 days",
    revoke: "Revoke",
    err: {
      email: "Enter a valid email address.",
      roleRequired: "Choose a role.",
      roleTooStrong: "You cannot give a role with permissions you do not have yourself.",
      memberTooStrong: "This member has permissions you do not have, so you cannot change their access.",
      self: "You cannot change your own roles or access.",
      alreadyMember: "This person is already a member of the organization.",
      limit: "Too many invitations today. Try again tomorrow.",
      notPending: "Only a pending invitation can be revoked.",
      permission: "You do not have permission to manage users.",
      invitationTooStrong: "This invitation grants permissions you do not have, so you cannot change it.",
    },
  },
  export: {
    button: "Export",
    hint: "Download the list as it is filtered now (Excel)",
    progress: "Exporting… {{rows}}",
    cancel: "Click to cancel",
    done_one: "Exported {{count}} row",
    done_few: "Exported {{count}} rows",
    done_many: "Exported {{count}} rows",
    done_other: "Exported {{count}} rows",
    truncated: "Only the first {{count}} rows were exported; narrow the filter.",
    failed: "Export failed. Try again.",
    active: "Active",
    warehouse: "Warehouse",
    allWarehouses: "All",
    locations: "Locations",
    supplierCode: "Supplier code",
    customerCode: "Customer code",
    lines: "Lines",
  },
  placeholder: {
    title: "{{page}} is on the way",
    body: "This screen belongs to the {{module}} module, which isn't built yet. Modules are delivered in the order defined in the roadmap.",
    backHome: "Back to Home",
  },
  listFilter: {
    menuLabel: "Sort and filter: {{column}}",
    open: "Sort and filter {{column}}",
    sortHint: "Click to sort; Alt+↓ opens sort and filter",
    sortAsc: {
      text: "Sort A → Z",
      number: "Sort smallest → largest",
      date: "Sort oldest → newest",
      enum: "Sort in order",
      bool: "Sort no → yes",
    },
    sortDesc: {
      text: "Sort Z → A",
      number: "Sort largest → smallest",
      date: "Sort newest → oldest",
      enum: "Sort in reverse order",
      bool: "Sort yes → no",
    },
    filter: "Filter",
    searchValues: "Search values…",
    selectAll: "(Select all)",
    noValues: "No values match.",
    any: "All",
    yes: "Yes",
    no: "No",
    condition: "Condition",
    value: "Value",
    from: "From",
    to: "To",
    clear: "Clear",
    apply: "Apply",
    active: "Active filters",
    remove: "Remove filter on {{column}}",
    clearAll: "Clear all",
    tooBroad: "This filter took too long on a large list. Add a status or date filter to narrow it down.",
    op: {
      text: {
        contains: "Contains",
        starts: "Starts with",
        eq: "Equals",
        empty: "Is empty",
        notempty: "Is not empty",
      },
      range: {
        eq: "Equals",
        ge: "At least (≥)",
        le: "At most (≤)",
        between: "Between",
        empty: "Is empty",
        notempty: "Is not empty",
      },
    },
    chip: {
      contains: "contains",
      starts: "starts with",
      eq: "=",
      ge: "≥",
      le: "≤",
      empty: "is empty",
      notempty: "is not empty",
    },
  },
  bomTree: {
    title: "BOM structure",
    item: "Item",
    direction: "Direction",
    structure: "Structure",
    whereUsed: "Where used",
    find: "Find in the tree…",
    pickTitle: "Pick an item",
    pickBody: "Choose a manufactured item to see its whole structure as a tree: every level is a column, links run from each item to its components.",
    noBomTitle: "No valid BOM",
    noBomBody: "This item has no approved BOM valid on the date. Approve a version on the BOM screen or change the date.",
    notUsedTitle: "Not used anywhere",
    notUsedBody: "No approved BOM valid on the date uses this item.",
    summary_one: "{{items}} items on {{count}} level",
    summary_few: "{{items}} items on {{count}} levels",
    summary_many: "{{items}} items on {{count}} levels",
    summary_other: "{{items}} items on {{count}} levels",
    leadTimeSummary: "cumulative lead time {{n}} d",
    detailLabel: "Tree item",
    top: "Top item",
    component: "Component",
    level: "Level {{n}}",
    usedIn: "Used in · {{n}}",
    levelLabel: "Level",
    showLevels: "Show levels",
    allLevels: "All",
    zoom: "Zoom",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    zoomReset: "Back to 100%",
    fit: "Fit to window",
    reset: "Reset view",
    treeLabel: "BOM structure tree",
    whereUsedLabel: "Where-used tree",
    make: "Manufactured",
    buy: "Purchased",
    makeShort: "Make",
    buyShort: "Buy",
    qtyPerParent: "Quantity per one of the parent",
    qtyPerChild: "Quantity of the item per one of this",
    qtyTotal: "Total for the exploded quantity",
    backflush: "Backflushed",
    expand: "Expand {{code}}",
    collapse: "Collapse {{code}}",
    expandHint: "Show {{count}} items below",
    keyboardHint: "↑↓ previous / next · ← parent or collapse · → components or expand · Enter details · Ctrl + wheel zoom",
    openBom: "Open BOM v{{v}}",
    showStructureOf: "Structure of {{code}}",
    showWhereUsedOf: "Where {{code}} is used",
    openItem: "Open the item",
    openAsTree: "Open as tree",
  },
  paste: {
    button: "Paste from Excel",
    hint: "Copy cells in Excel (item code, quantity…) and press Ctrl+V in the lines, or click here",
    previewLabel: "Pasted lines",
    found_one: "{{count}} line ready to add",
    found_few: "{{count}} lines ready to add",
    found_many: "{{count}} lines ready to add",
    found_other: "{{count}} lines ready to add",
    noneValid: "No line can be added",
    skipped_one: "{{count}} row skipped",
    skipped_few: "{{count}} rows skipped",
    skipped_many: "{{count}} rows skipped",
    skipped_other: "{{count}} rows skipped",
    more_one: "…and {{count}} more",
    more_few: "…and {{count}} more",
    more_many: "…and {{count}} more",
    more_other: "…and {{count}} more",
    row: "Row {{row}}",
    add_one: "Add {{count}} line",
    add_few: "Add {{count}} lines",
    add_many: "Add {{count}} lines",
    add_other: "Add {{count}} lines",
    added_one: "Added {{count}} line — check it and save",
    added_few: "Added {{count}} lines — check them and save",
    added_many: "Added {{count}} lines — check them and save",
    added_other: "Added {{count}} lines — check them and save",
    truncated: "Only the first {{count}} rows were read.",
    nothing: "The clipboard has no cells to paste.",
    failed: "Couldn't look up the item codes. Try again.",
    useShortcut: "Click into the lines and press Ctrl+V (the browser didn't allow reading the clipboard).",
    issue: {
      noCode: "no item code",
      unknown: "no item {{code}}",
      inactive: "item {{code}} is inactive",
      notAllowed: "item {{code}} can't be used here",
      qty: "quantity \"{{value}}\" is not a positive number",
      price: "price \"{{value}}\" is not a number",
      date: "date \"{{value}}\" is not a date",
      scrap: "scrap \"{{value}}\" is not a number",
    },
  },
  mass: {
    check: "Check",
    willChange: "Will change",
    alreadySet: "Already set",
    willSkip: "Skipped",
    skipReasons: "Skipped, by reason",
    examples: "For example",
    nothingToChange: "Nothing to change: every row is already set or skipped.",
    capped: "More than {{max}} rows match. Narrow the filter and check again.",
    running: "Applying…",
    progress: "{{done}} of {{total}}",
    soFar: "{{changed}} changed · {{unchanged}} already set · {{skipped}} skipped",
    stop: "Stop after this batch",
    finished: "Done",
    stopped: "Stopped — the rows not yet processed are unchanged",
    changed: "Changed",
    unchanged: "Already set",
    skipped: "Skipped",
    moreSkipped: "…and {{count}} more in the export",
    exportSkipped: "Export skipped rows ({{count}})",
    skippedSheet: "Skipped",
    colCode: "Code",
    colReason: "Reason",
    close: "Close",
    checkedRows_one: "{{count}} checked item",
    checkedRows_few: "{{count}} checked items",
    checkedRows_many: "{{count}} checked items",
    checkedRows_other: "{{count}} checked items",
    allItems: "All items matching the list filter",
    yes: "Yes",
    no: "No",
    leaveHint: "Only the parameters you tick change; the others stay as they are on each item.",
    clearValue: "Empty = clear",
    reasons: {
      service: "Service — no planning",
      inactive: "Inactive item",
      not_purchased: "Not purchased — a supplier applies to purchased items only",
      decimals: "Quantity finer than the item's unit allows",
      has_stock: "Still has stock",
      on_count: "On a running cycle count",
      in_progress: "Production has started — output is reported",
      finished: "The order is completed or closed",
      cancelled: "The document is cancelled",
      no_materials: "The order has no material line",
      item_inactive: "The item is inactive",
      has_postings: "Output reported or material issued — complete it instead of cancelling",
      date_range: "New date out of range: at most a year back and ten years ahead",
      moves_with_parent: "Moves with its parent order in the kit",
      parent_not_moved: "Its parent order in the kit did not move — it moves only together with it",
      changed_since_check: "Changed by someone after the check — check again",
      kit_conflict: "The move would break the kit's links — move this order on its own, with confirmation",
      has_receipts: "Goods were received on this order — close it instead of cancelling",
      no_lines: "The document has no line",
      supplier_inactive: "The supplier is inactive",
      lines_incomplete: "A line has no price or promised date, or its item is inactive",
      subcontract_follows_operation: "Only subcontract lines are open — they follow their operation; move the production order",
      no_open_so_lines: "No open line with a requested date",
      no_open_lines: "No open line with a promised date",
      customer_inactive: "The customer is inactive",
      so_lines_incomplete: "A line has no price or requested date, or its item is inactive",
      has_shipments: "Goods were shipped on this order — close it instead of cancelling",
      not_released: "A draft — cancel it instead of closing",
      converted: "Already converted into a document",
      not_supplier: "Not a supplier — supplier fields apply to suppliers only",
      not_customer: "Not a customer — payment terms apply to customers only",
      warehouse_inactive: "The warehouse is inactive",
      quarantine_location: "The warehouse's quarantine location must stay active, receivable, not pickable and not blocked",
      default_location: "The DEFAULT location keeps its flags — postings without a bin land there",
      refused: "Refused by a document rule",
      posted: "Already posted — a posted document stays",
      company_inactive: "The company is inactive",
    },
    items: {
      planning: "Planning parameters",
      planningTitle: "Change planning parameters",
      deactivate: "Deactivate",
      deactivateTitle: "Deactivate items",
      deactivateHint: "Items with stock or on a running cycle count are skipped. Inactive items cannot be ordered or planned.",
      activate: "Activate",
      activateTitle: "Activate items",
      applyPlanning_one: "Change {{count}} item",
      applyPlanning_few: "Change {{count}} items",
      applyPlanning_many: "Change {{count}} items",
      applyPlanning_other: "Change {{count}} items",
      applyDeactivate_one: "Deactivate {{count}} item",
      applyDeactivate_few: "Deactivate {{count}} items",
      applyDeactivate_many: "Deactivate {{count}} items",
      applyDeactivate_other: "Deactivate {{count}} items",
      applyActivate_one: "Activate {{count}} item",
      applyActivate_few: "Activate {{count}} items",
      applyActivate_many: "Activate {{count}} items",
      applyActivate_other: "Activate {{count}} items",
    },
    productionOrders: {
      checkedRows_one: "{{count}} checked order",
      checkedRows_few: "{{count}} checked orders",
      checkedRows_many: "{{count}} checked orders",
      checkedRows_other: "{{count}} checked orders",
      all: "All orders matching the list filter",
      release: "Release",
      releaseTitle: "Release production orders",
      releaseHint: "Only planned orders are released. Each goes through the same release as a single one: material reservation, subcontracting lines, events and audit.",
      cancel: "Cancel orders",
      cancelTitle: "Cancel production orders",
      cancelHint: "Only planned and released orders without reported output or issued material are cancelled; material reservations are released. Orders with postings are skipped.",
      move: "Reschedule",
      moveTitle: "Reschedule production orders",
      moveHint: "Start and due move together, the duration stays. Orders in progress or finished are skipped; a kit's sub-orders move with their parent order.",
      moveBy: "Shift by days",
      moveByHint: "A negative number = earlier",
      moveTo: "Set a new start date",
      days: "Number of days",
      newStart: "New start",
      applyRelease_one: "Release {{count}} order",
      applyRelease_few: "Release {{count}} orders",
      applyRelease_many: "Release {{count}} orders",
      applyRelease_other: "Release {{count}} orders",
      applyCancel_one: "Cancel {{count}} order",
      applyCancel_few: "Cancel {{count}} orders",
      applyCancel_many: "Cancel {{count}} orders",
      applyCancel_other: "Cancel {{count}} orders",
      applyMove_one: "Reschedule {{count}} order",
      applyMove_few: "Reschedule {{count}} orders",
      applyMove_many: "Reschedule {{count}} orders",
      applyMove_other: "Reschedule {{count}} orders",
    },
    transfers: {
      checkedRows_one: "{{count}} checked transfer",
      checkedRows_few: "{{count}} checked transfers",
      checkedRows_many: "{{count}} checked transfers",
      checkedRows_other: "{{count}} checked transfers",
      all: "All transfers matching the list filter",
      post: "Post drafts",
      postTitle: "Post transfers",
      postHint: "Only drafts are posted, one at a time, each through the same posting as a single one: stock checks, lots and serials, locations, audit. A transfer the inventory refuses (stock short, a blocked location) is skipped with the reason and stays a draft.",
      cancel: "Cancel drafts",
      cancelTitle: "Cancel transfers",
      cancelHint: "Only drafts are cancelled. Posted transfers stay — to undo one, post a transfer back.",
      applyPost_one: "Post {{count}} transfer",
      applyPost_few: "Post {{count}} transfers",
      applyPost_many: "Post {{count}} transfers",
      applyPost_other: "Post {{count}} transfers",
      applyCancel_one: "Cancel {{count}} transfer",
      applyCancel_few: "Cancel {{count}} transfers",
      applyCancel_many: "Cancel {{count}} transfers",
      applyCancel_other: "Cancel {{count}} transfers",
    },
    salesOrders: {
      move: "Shift dates",
      moveTitle: "Shift requested dates",
      moveHint: "Every open line's requested date moves by the same number of days (the customer moved the delivery). Partially shipped lines move too; shipped and closed lines keep their dates. Reservations stay.",
      moveBy: "Shift by days",
      moveByHint: "A negative number = earlier",
      days: "Number of days",
      applyMove_one: "Shift {{count}} order",
      applyMove_few: "Shift {{count}} orders",
      applyMove_many: "Shift {{count}} orders",
      applyMove_other: "Shift {{count}} orders",
      checkedRows_one: "{{count}} checked order",
      checkedRows_few: "{{count}} checked orders",
      checkedRows_many: "{{count}} checked orders",
      checkedRows_other: "{{count}} checked orders",
      all: "All orders matching the list filter",
      release: "Release",
      releaseTitle: "Release sales orders",
      releaseHint: "Only draft orders are released. Each goes through the same release as a single one: active customer, a price and requested date on every line, stock reserved, audit.",
      cancel: "Cancel orders",
      cancelTitle: "Cancel sales orders",
      cancelHint: "Only draft and released orders with nothing shipped are cancelled; their reservations are released. Orders with a shipment are skipped — close them instead.",
      close: "Close orders",
      closeTitle: "Close sales orders",
      closeHint: "Released, partially shipped and shipped orders are closed: open lines stop counting as demand and reservations are released. Drafts are skipped — cancel them instead.",
      applyRelease_one: "Release {{count}} order",
      applyRelease_few: "Release {{count}} orders",
      applyRelease_many: "Release {{count}} orders",
      applyRelease_other: "Release {{count}} orders",
      applyCancel_one: "Cancel {{count}} order",
      applyCancel_few: "Cancel {{count}} orders",
      applyCancel_many: "Cancel {{count}} orders",
      applyCancel_other: "Cancel {{count}} orders",
      applyClose_one: "Close {{count}} order",
      applyClose_few: "Close {{count}} orders",
      applyClose_many: "Close {{count}} orders",
      applyClose_other: "Close {{count}} orders",
    },
    plannedOrders: {
      checkedRows_one: "{{count}} checked planned order",
      checkedRows_few: "{{count}} checked planned orders",
      checkedRows_many: "{{count}} checked planned orders",
      checkedRows_other: "{{count}} checked planned orders",
      all: "All planned orders matching the list filter",
      firm: "Firm",
      firmTitle: "Firm planned orders",
      firmHint: "Firmed orders stay through the next MRP run. Optionally shift due and start by the same days (a firmed order is moved too). Converted orders are skipped.",
      shiftBy: "Shift by days (optional)",
      shiftByHint: "Empty = keep the dates; a negative number = earlier",
      days: "Number of days",
      delete: "Delete",
      deleteTitle: "Delete planned orders",
      deleteHint: "Planned and firmed suggestions are deleted; the next MRP run proposes again what is still needed. Converted orders are skipped.",
      applyFirm_one: "Firm {{count}} order",
      applyFirm_few: "Firm {{count}} orders",
      applyFirm_many: "Firm {{count}} orders",
      applyFirm_other: "Firm {{count}} orders",
      applyDelete_one: "Delete {{count}} order",
      applyDelete_few: "Delete {{count}} orders",
      applyDelete_many: "Delete {{count}} orders",
      applyDelete_other: "Delete {{count}} orders",
    },
    partners: {
      update: "Change in bulk",
      updateTitle: "Change partners",
      updateHint: "Tick the fields to change. Supplier fields change only on suppliers, payment terms only on customers; others are skipped with the reason.",
      checkedRows_one: "{{count}} checked partner",
      checkedRows_few: "{{count}} checked partners",
      checkedRows_many: "{{count}} checked partners",
      checkedRows_other: "{{count}} checked partners",
      all: "All partners matching the list filter",
      active: "Active",
      currency: "Currency (ISO code)",
      leadTime: "Supplier lead time (days)",
      tolerance: "Receipt tolerance (%)",
      paymentTerms: "Customer payment terms",
      apply_one: "Change {{count}} partner",
      apply_few: "Change {{count}} partners",
      apply_many: "Change {{count}} partners",
      apply_other: "Change {{count}} partners",
    },
    locations: {
      update: "Change in bulk",
      updateTitle: "Change locations",
      updateHint: "Tick the fields to change. A location with stock is not deactivated; the warehouse's quarantine location stays active, receivable, not pickable and not blocked.",
      checkedRows_one: "{{count}} checked location",
      checkedRows_few: "{{count}} checked locations",
      checkedRows_many: "{{count}} checked locations",
      checkedRows_other: "{{count}} checked locations",
      all: "All locations of {{warehouse}} matching the list filter",
      active: "Active",
      receivable: "Receivable",
      pickable: "Pickable",
      blocked: "Blocked",
      zone: "Zone",
      apply_one: "Change {{count}} location",
      apply_few: "Change {{count}} locations",
      apply_many: "Change {{count}} locations",
      apply_other: "Change {{count}} locations",
    },
    purchaseOrders: {
      checkedRows_one: "{{count}} checked order",
      checkedRows_few: "{{count}} checked orders",
      checkedRows_many: "{{count}} checked orders",
      checkedRows_other: "{{count}} checked orders",
      all: "All orders matching the list filter",
      release: "Release",
      releaseTitle: "Release purchase orders",
      releaseHint: "Only draft orders are released. Each goes through the same release as a single one: active supplier, a price and promised date on every line, audit.",
      cancel: "Cancel orders",
      cancelTitle: "Cancel purchase orders",
      cancelHint: "Only draft and released orders with nothing received are cancelled; their lines stop counting as open supply. Orders with a receipt are skipped — close them instead.",
      applyRelease_one: "Release {{count}} order",
      applyRelease_few: "Release {{count}} orders",
      applyRelease_many: "Release {{count}} orders",
      applyRelease_other: "Release {{count}} orders",
      applyCancel_one: "Cancel {{count}} order",
      applyCancel_few: "Cancel {{count}} orders",
      applyCancel_many: "Cancel {{count}} orders",
      applyCancel_other: "Cancel {{count}} orders",
      move: "Shift dates",
      moveTitle: "Shift promised dates",
      moveHint: "Every open line's promised date moves by the same number of days (the supplier confirmed a delay or an earlier delivery). Received lines and subcontract lines keep their dates.",
      moveBy: "Shift by days",
      moveByHint: "A negative number = earlier",
      days: "Number of days",
      applyMove_one: "Shift {{count}} order",
      applyMove_few: "Shift {{count}} orders",
      applyMove_many: "Shift {{count}} orders",
      applyMove_other: "Shift {{count}} orders",
    },
  },
  listTools: {
    label: "{{list}} tools",
    new: "New",
    duplicate: "Duplicate as new",
    delete: "Delete",
    refresh: "Refresh",
    print: "Print list",
    find: "Find",
    copyRows: "Copy rows",
    copyRowsHint: "checked or loaded rows, paste into Excel",
    first: "First record",
    prev: "Previous record",
    next: "Next record",
    last: "Last loaded record",
    columns: "Columns",
    columnsTitle: "Show columns",
    showAllColumns: "Show all columns",
    more: "More list actions",
    selectAll: "Check all loaded rows",
    clearChecked: "Clear check marks",
    selectAllMatching: "Select all rows matching the filter",
    allMatching: "All rows matching the filter",
    clearFilters: "Clear filters and sort",
    copyLink: "Copy link to this view",
    compact: "Compact rows",
    comfortable: "Comfortable rows",
    shortcuts: "Keyboard shortcuts",
    hide: "Hide",
    keyMove: "move",
    keyCheck: "check row",
    keyClose: "close details",
    keyboardHint: "↑↓ move · Space check · / find · Esc close",
    notAvailable: "not available on this list",
    forChecked_one: "{{count}} checked row",
    forChecked_few: "{{count}} checked rows",
    forChecked_many: "{{count}} checked rows",
    forChecked_other: "{{count}} checked rows",
    checked_one: "{{count}} checked",
    checked_few: "{{count}} checked",
    checked_many: "{{count}} checked",
    checked_other: "{{count}} checked",
    checkRow: "Check row {{n}}",
    copied_one: "Copied {{count}} row — paste into Excel",
    copied_few: "Copied {{count}} rows — paste into Excel",
    copied_many: "Copied {{count}} rows — paste into Excel",
    copied_other: "Copied {{count}} rows — paste into Excel",
    copyFailed: "Couldn't copy to the clipboard",
    rows_one: "{{count}} row",
    rows_few: "{{count}} rows",
    rows_many: "{{count}} rows",
    rows_other: "{{count}} rows",
    checkedOnly: "checked rows only",
    preparing: "Preparing the printout…",
    printTruncated: "Only the first {{count}} rows are printed; narrow the filter or export to Excel.",
    done_one: "{{action}}: {{count}} row",
    done_few: "{{action}}: {{count}} rows",
    done_many: "{{action}}: {{count}} rows",
    done_other: "{{action}}: {{count}} rows",
    failed: "That didn't work. Try again.",
    mixedUnits: "different units",
    mixedUnitsHint: "The checked rows have different units or currencies; check rows of one unit to see a total.",
    yes: "Yes",
    no: "No",
  },
  docActions: {
    label: "Document actions",
    duplicate: "Duplicate as new",
    duplicateOrder: "Duplicate as new order",
    copyOf: "Copy of {{number}}",
    copyNumber: "Copy number",
    copyLink: "Copy link",
    numberCopied: "Number copied",
    linkCopied: "Link copied",
  },
  lineActions: {
    duplicate: "Duplicate line {{n}}",
    insertBelow: "Insert line below",
    moveUp: "Move up",
    moveDown: "Move down",
    delete: "Delete line {{n}}",
    more: "More actions for line {{n}}",
  },
  process: {
    pathLabel: "Process path",
    stepAria: "Step {{n}} of {{total}}: {{label}} ({{state}})",
    state: {
      done: "done",
      current: "current step",
      todo: "not yet",
    },
    exit: {
      cancelled: "Cancelled — the process ended here, nothing more is posted.",
      obsolete: "Obsolete — replaced by a newer version, kept for history.",
    },
    finished: "Finished",
    explain: "Explain steps",
    finishedHint: "Every step is done. The document is kept read-only for history and traceability.",
    step: {
      purchase_order: {
        draft: "Draft",
        released: "Released",
        receiving: "Receiving",
        received: "Received",
        closed: "Closed",
      },
      sales_order: {
        draft: "Draft",
        released: "Released",
        shipping: "Shipping",
        shipped: "Shipped",
        closed: "Closed",
      },
      production_order: {
        planned: "Planned",
        released: "Released",
        in_progress: "In progress",
        completed: "Completed",
        closed: "Closed",
      },
      stock_adjustment: {
        draft: "Draft",
        posted: "Posted",
      },
      stock_transfer: {
        draft: "Draft",
        posted: "Posted",
      },
      stock_count: {
        draft: "Draft",
        counting: "Counting",
        posted: "Posted",
      },
      inspection_order: {
        open: "Open",
        results: "Results",
        decided: "Decided",
        closed: "Closed",
      },
      nonconformance_report: {
        open: "Open",
        dispositioned: "Dispositioned",
        closed: "Closed",
      },
      project: {
        draft: "Draft",
        active: "Active",
        completed: "Completed",
        closed: "Closed",
      },
      planned_order: {
        planned: "Planned",
        firmed: "Firmed",
        converted: "Converted",
      },
      version: {
        draft: "Draft",
        approved: "Approved",
      },
    },
    hint: {
      purchase_order: {
        draft: "Enter supplier, lines and dates. Nothing is ordered yet — release the order when it is complete.",
        released: "The order is sent to the supplier and counts as incoming supply in planning. Next: receive the goods.",
        receiving: "Part of the quantity has arrived. Receive the rest, or close the order if no more is coming.",
        received: "Everything ordered has been received. Close the order to end it.",
        closed: "The order is finished; open quantities no longer count as supply.",
      },
      sales_order: {
        draft: "Enter customer, lines and dates. Nothing is reserved yet — release the order when it is complete.",
        released: "Stock is reserved and the order counts as demand in planning. Next: pick and ship.",
        shipping: "Part of the quantity has shipped. Ship the rest, or close the order.",
        shipped: "Everything ordered has shipped. Close the order to end it.",
        closed: "The order is finished; reservations are released.",
      },
      production_order: {
        planned: "Quantity, dates, BOM and routing are set. Release the order to start production.",
        released: "Materials are reserved and the shop floor can start. Next: issue materials or report the first quantity.",
        in_progress: "Production is running: issue materials, book operation hours and report finished goods.",
        completed: "The planned quantity is reported. Check consumption and close the order.",
        closed: "The order is finished; remaining reservations are released.",
      },
      stock_adjustment: {
        draft: "Enter items, locations and quantities (+ in, − out). Stock does not change until you post.",
        posted: "Stock is updated in the inventory ledger. Corrections are made with a new document.",
      },
      stock_transfer: {
        draft: "Choose item, quantity, from and to location. Stock does not move until you post.",
        posted: "Stock moved in the inventory ledger. To undo, post a transfer back.",
      },
      stock_count: {
        draft: "Choose the scope (warehouse, locations, items). Start the count to freeze the expected quantities.",
        counting: "Enter the counted quantities. Posting books the differences as adjustments.",
        posted: "Differences are posted to the inventory ledger.",
      },
      inspection_order: {
        open: "Created from a receipt, production report or on demand. The lot waits for inspection.",
        results: "Enter a result for every characteristic of the inspection plan.",
        decided: "Accepted, rejected or partly accepted. Rejected quantity goes to quarantine with a nonconformance report.",
        closed: "The inspection is finished; the lot quality status is final.",
      },
      nonconformance_report: {
        open: "Describe the defect and the quantity. Then choose a disposition.",
        dispositioned: "Disposition chosen (use as is, rework, scrap, return). Carry it out, then close the report.",
        closed: "The nonconformance is handled.",
      },
      project: {
        draft: "Set up the work breakdown (phases, activities) and planned hours. Activate when ready.",
        active: "Orders can be pegged to the project; stock and reservations are kept per project.",
        completed: "Work is done; no new pegging. Move remaining project stock to free stock, then close.",
        closed: "The project is finished and kept for history.",
      },
      planned_order: {
        planned: "Suggested by MRP; the next run may change or remove it. Firm it to keep it, or convert it.",
        firmed: "Kept by MRP as it is. Convert it into a purchase or production order.",
        converted: "Turned into a real order; follow it there.",
      },
      version: {
        draft: "Edit the structure freely. Approve it to make it available to orders and planning.",
        approved: "In use by orders and planning; it can no longer be edited. Copy it to a new version to change.",
      },
    },
  },
  guide: {
    title: "Where am I?",
    home: "Home — pick a process below to see its screens.",
    close: "Close process guide",
    youAreHere: "You are here",
    yourProcesses: "Processes of this screen",
    otherProcesses: "Other processes",
    notInProcess: "This screen is not part of a process map. Pick a process below.",
    openMap: "Open the full process map",
    shortcuts: "Keyboard shortcuts",
    keys: {
      palette: "Command palette: jump to any screen or session code",
      guide: "This process guide",
      search: "Search in the list",
      rows: "Move through list rows (Enter opens)",
      close: "Close the open panel or dialog",
      duplicateLine: "Duplicate the line you are editing",
      moveLine: "Move the line up / down (BOM, project structure)",
    },
    processes: {
      procureToStock: {
        title: "Procure to stock",
        summary: "From a supplier order to goods on the shelf.",
        stages: {
          masterData: "Set up the supplier and the items you buy.",
          order: "Create the purchase order and release it to the supplier.",
          receive: "Receive the delivery against the order; stock goes to the receiving location.",
          inspect: "If the item needs inspection, decide accept or reject — the lot stays blocked until then.",
          stock: "Check what is on hand, where, and which lots.",
          returnToSupplier: "Send goods back to the supplier against the received order (rejected goods from quarantine too).",
        },
      },
      orderToDelivery: {
        title: "Order to delivery",
        summary: "From a customer order to a shipped delivery.",
        stages: {
          masterData: "Set up the customer and the items you sell.",
          order: "Create the sales order and release it — stock gets reserved.",
          pick: "Pick the reserved stock from its locations.",
          ship: "Ship the picked goods; the order shows shipped quantities.",
          return: "Take goods back on an RMA against the shipped order; inspected lines go to quarantine.",
        },
      },
      planToProduce: {
        title: "Plan to produce",
        summary: "From demand to finished goods.",
        stages: {
          engineering: "Define what is made and how: approved BOM, routing and work centers.",
          mrp: "Run MRP: it nets demand against stock and supply per item and project.",
          plannedOrders: "Review the suggestions, firm the ones you keep and convert them to orders.",
          schedule: "Schedule kits on the master plan: move a kit and every order in it follows; check availability (ATP / CTP) before release.",
          produce: "Release the production order, issue materials, book hours and report output.",
          stock: "Finished goods are on hand (after inspection, if required).",
        },
      },
      inventoryControl: {
        title: "Inventory control",
        summary: "Keep stock correct and where it belongs.",
        stages: {
          structure: "Set up warehouses, locations and adjustment reasons.",
          move: "Move stock between locations, or adjust it with a reason.",
          count: "Count locations; posting books the differences.",
          stock: "Check balances, lots and their quality status.",
        },
      },
      quality: {
        title: "Quality",
        summary: "Inspect what comes in and goes out, handle what fails.",
        stages: {
          plans: "Define the characteristics and which items or suppliers need inspection.",
          inspect: "Enter results and decide: accept, reject or partial. Stock found on the floor is blocked from Inventory (spot check).",
          nonconformance: "For rejected quantity: choose a disposition and close the report.",
        },
      },
      project: {
        title: "Project",
        summary: "Run customer projects with their own stock and progress.",
        stages: {
          setup: "Create the project and its work breakdown, then activate it.",
          peg: "Peg sales, purchase and production orders to the project.",
          time: "Book hours on project activities.",
          progress: "Follow hours, milestones and order progress.",
        },
      },
      setup: {
        title: "Company setup",
        summary: "What has to exist before documents can be created.",
        stages: {
          organization: "Companies, sites and the users with their roles.",
          numbering: "Number series for every document type.",
          units: "Units of measure and conversions.",
          items: "Items and business partners.",
          audit: "Every change is recorded in the audit log.",
        },
      },
    },
  },
  processMap: {
    stepShort: "Step {{n}}",
    views: { map: "Map", scenarios: "Scenarios", list: "List" },
    viewsLabel: "Views",
    search: "Find a step or screen",
    searchNone: "No step matches “{{q}}”.",
    searchCount_one: "{{count}} step",
    searchCount_few: "{{count}} steps",
    searchCount_many: "{{count}} steps",
    searchCount_other: "{{count}} steps",
    workTitle: "Waiting now",
    workHint: "Open documents of the active company waiting at this step.",
    workMore: "99+",
    work: {
      po_draft: "draft purchase orders to release",
      po_to_receive: "purchase orders to receive",
      so_draft: "draft sales orders to release",
      so_to_ship: "sales orders to pick and ship",
      planned_open: "planned orders to convert",
      mo_planned: "production orders to schedule and release",
      mo_active: "production orders in production",
      inspections_open: "inspections to decide",
      ncr_open: "nonconformances to handle",
      moves_draft: "draft transfers and adjustments",
      counts_open: "cycle counts open",
      projects_active: "active projects",
    },
    openList: "Open the list",
    player: {
      label: "Scenario",
      choose: "Walk through a scenario",
      prev: "Previous step",
      next: "Next step",
      play: "Play",
      pause: "Pause",
      exit: "Close the scenario",
      step: "Step {{n}} of {{total}}",
      done: "Done — that is the whole scenario.",
      via: "Hand-off",
    },
    scenariosIntro: "Typical business cases, walked step by step across the processes. Start one to see each step light up on the map with what happens there and where to do it.",
    start: "Start on the map",
    stepsCount: "{{count}} steps",
    scenarios: {
      makeToOrder: { title: "Make to order", summary: "A customer order drives MRP, production, the product inspection and the delivery." },
      buyToStock: { title: "Buy to stock", summary: "MRP proposes a purchase; the order is placed, received, inspected and put on the shelf." },
      qualityReject: { title: "Rejected delivery", summary: "A receipt fails inspection: quarantine, a nonconformance report and its disposition." },
      projectDelivery: { title: "Project delivery", summary: "A customer project: orders pegged to it, project stock, hours and progress." },
      inventoryCheck: { title: "Keep stock right", summary: "Warehouse structure, moves and adjustments, cycle counts and the stock overview." },
      companySetup: { title: "Set up a company", summary: "What has to exist before the first document: organisation, numbering, units, items, warehouses, quality plans." },
    },
    list: {
      label: "All steps",
      process: "Process",
      step: "Step",
      what: "What happens",
      screens: "Screens",
      posts: "Posts stock",
      work: "Waiting",
    },
    title: "Process map",
    introVisual: "How Patronix works end to end. Each row is a process, each card a step. Hover or click a step to see where it comes from and where it leads; pick a process to follow it alone.",
    filter: "Show processes",
    allProcesses: "All processes",
    showHandoffs: "Show all hand-offs",
    ledgerHintVisual: "Every step with this badge posts stock here — hover to light them up. Rows are never changed; balances come from them.",
    legendTitle: "Legend",
    details: "Step details",
    pickStage: "Click a step to see what happens there, which screens to open and what comes before and after it.",
    stepOf: "step {{n}} of {{total}}",
    closeDetails: "Close step details",
    screens: "Screens",
    comesFrom: "Comes from",
    leadsTo: "Leads to",
    columns: {
      c0: "Master data",
      c1: "Demand",
      c2: "Plan",
      c3: "Order",
      c4: "Execute",
      c5: "Check",
      c6: "Result",
    },
    legend: {
      posts: "Posts to the inventory ledger",
      document: "Document the user creates",
      automatic: "Automatic link",
      hover: "Hover a step or the ledger to highlight what belongs together.",
    },
    ledger: "Inventory ledger",
    stages: {
      masterData: "Master data",
      order: "Order",
      receive: "Receive",
      inspect: "Inspect",
      stock: "Stock",
      pick: "Pick",
      ship: "Ship",
      return: "Return (RMA)",
      returnToSupplier: "Return to supplier",
      engineering: "Engineering",
      mrp: "MRP",
      plannedOrders: "Planned orders",
      schedule: "Schedule (APS)",
      produce: "Produce",
      structure: "Structure",
      move: "Move / adjust",
      count: "Count",
      plans: "Plans",
      nonconformance: "Nonconformance",
      setup: "Set up",
      peg: "Peg orders",
      time: "Time",
      progress: "Progress",
      organization: "Organization",
      numbering: "Numbering",
      units: "Units",
      items: "Items & partners",
      audit: "Audit",
    },
    links: {
      salesDemand: "Released sales orders are demand for MRP.",
      plannedPurchase: "Planned purchases are converted to purchase orders (or proposed by the purchasing agent).",
      planStale: "Moving a kit marks the MRP plan stale until the next run.",
      receiptInspection: "A receipt of an item with an inspection plan creates an inspection order; the lot is blocked.",
      reportInspection: "A production report of an item with an inspection plan creates a product inspection.",
      returnInspection: "A return line marked for inspection is received into quarantine with an inspection order.",
      ncrReturn: "A nonconformance with the disposition return to supplier creates a return; its shipment closes the report.",
      disposition: "Rejected stock goes to quarantine; the disposition moves, scraps or returns it.",
      pegSales: "Sales order lines pegged to a project reserve project stock.",
      pegPurchase: "Purchase order lines pegged to a project receive into project stock.",
      pegProduction: "Production orders pegged to a project consume and report project stock.",
      subcontractOrder: "Releasing a production order creates a subcontract purchase order line for each operation run at a supplier.",
      subcontractReceipt: "Receiving the subcontracted work reports the operation on the production order (no stock moves).",
      subcontractMaterial: "Sending material for a subcontracted operation is a stock transfer into the subcontractor warehouse.",
      plannedSubcontract: "Planned subcontract orders become purchase order lines to the subcontractor, with the materials it needs in its warehouse.",
      subcontractItemMaterial: "The material of an item-subcontracting order line goes to the subcontractor as a stock transfer; receiving the item consumes it there.",
    },
  },
  notFound: {
    title: "Page not found",
    body: "The address doesn't match any screen.",
  },
} as const;

export default en;

type Widen<T> = { -readonly [K in keyof T]: T[K] extends string ? string : Widen<T[K]> };
export type Messages = Widen<typeof en>;
